import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Clock, Plus, Edit, Trash2, Save, ArrowLeft, Search, Users, User, Calendar, Eye, Settings, FileText, CheckCircle, Timer, X, AlertCircle, Download, ToggleLeft, ToggleRight, ArrowUpDown, ArrowUp, ArrowDown, RotateCcw
} from "lucide-react";
import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";
import { useNotification } from "../../../components/common/NotificationProvider";
import { formatDate } from "../../../utils/stringUtils";
import { exportToCSV, generateExportFilename, downloadFromBackend } from '../../../utils/exportUtils';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import DateRangeFilter from "../../../components/common/DateRangeFilter";
import WarningPopup from "../../../components/WarningPopup";
import { INVALID_CHARS_REGEX } from "../../../utils/formValidation";
import PaginationControls from "../../../components/common/Pagination/PaginationControls";

const TEXT_LIMITS = {
  shiftName: 60,
  description: 500,
};

const NUMBER_LIMITS = {
  breakDuration: 480,
  maxEmployees: 10000,
};

const sanitizeAlphaNumericComma = (value = "", limit) => {
  const sanitized = value.replace(INVALID_CHARS_REGEX, "");
  if (typeof limit === "number") {
    return sanitized.slice(0, limit);
  }
  return sanitized;
};

const sanitizeAlphaNumericCommaAllowedSymbols = (value = "", limit) => {
  const sanitized = value.replace(INVALID_CHARS_REGEX, "");
  if (typeof limit === "number") {
    return sanitized.slice(0, limit);
  }
  return sanitized;
};

const sanitizeAlphaNumericCommaHyphen = (value = "", limit) => {
  const sanitized = value.replace(INVALID_CHARS_REGEX, "");
  if (typeof limit === "number") {
    return sanitized.slice(0, limit);
  }
  return sanitized;
};

const sanitizePositiveNumber = (value = "", maxValue) => {
  if (value === null || value === undefined) return "";
  const digitsOnly = String(value).replace(/[^\d]/g, "");
  if (digitsOnly === "") return "";
  let numericValue = parseInt(digitsOnly, 10);
  if (Number.isNaN(numericValue) || numericValue < 0) {
    numericValue = 0;
  }
  if (typeof maxValue === "number" && numericValue > maxValue) {
    numericValue = maxValue;
  }
  return numericValue.toString();
};

const formatTimeAMPM = (timeStr) => {
  if (!timeStr || timeStr === 'N/A') return "";
  const str = String(timeStr).trim();
  if (str.includes('AM') || str.includes('PM')) return str;

  if (str.includes('T')) {
    const parsed = new Date(str);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
    }
  }

  try {
    const parts = str.split(':');
    if (parts.length < 2) return str;

    const hours = parts[0];
    const minutes = parts[1];
    if (hours === undefined || minutes === undefined) return str;

    const h = parseInt(hours, 10);
    const m = parseInt(minutes, 10);

    if (isNaN(h) || isNaN(m)) return str;

    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
  } catch (e) {
    return str || "";
  }
};

const ShiftRoster = ({ onBack, userRole, initialMode = "list", id, onClose }) => {
  const { showSuccess, showError, showWarning, showInfo } = useNotification();
  // Get user role from localStorage if not provided as prop
  const [currentUserRole, setCurrentUserRole] = useState(
    userRole || "super_admin"
  );
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [roster, setRoster] = useState([]);
  const [filteredRoster, setFilteredRoster] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showShiftForm, setShowShiftForm] = useState(initialMode === "create_shift" || initialMode === "edit_shift");
  const [showRosterForm, setShowRosterForm] = useState(initialMode === "assign_roster" || initialMode === "edit_roster");
  const [editingShift, setEditingShift] = useState(null);
  const [editingRoster, setEditingRoster] = useState(null);
  const [selectedRoster, setSelectedRoster] = useState(null);
  const [showDetails, setShowDetails] = useState(initialMode === "view_roster");
  const [selectedShiftDetail, setSelectedShiftDetail] = useState(null);
  const [showShiftDetail, setShowShiftDetail] = useState(initialMode === "view_shift");
  const [viewMode, setViewMode] = useState(initialMode?.includes("shift") ? "shifts" : "roster"); // roster, shifts
  const [filterDepartment, setFilterDepartment] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all"); // all, Active, Upcoming, Completed
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [activeTab, setActiveTab] = useState("current"); // current, upcoming, history
  const [showManualAssignment, setShowManualAssignment] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [shiftFormData, setShiftFormData] = useState({
    name: "",
    startTime: "",
    endTime: "",
    breakDuration: "",
    workingHours: "",
    description: "",
    isActive: true,
    shiftType: "Regular",
    workingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    weeklyHolidays: ['saturday', 'sunday'],
    saturdayWorking: [],
    isFlexible: false,
    graceCheckInTime: "0",
    graceDays: "0",
    graceDaysTime: "0",
    graceCheckOutTime: "0",
    halfDayRule: "0",
    absentRule: "0",
  });
  const [rosterFormData, setRosterFormData] = useState({
    employeeId: "",
    shiftId: "",
    startDate: "",
    endDate: "",
    isRecurring: false,
    recurringDays: [],
    notes: "",
    assignmentType: "Permanent",
  });
  const [bulkFormData, setBulkFormData] = useState({
    department: "",
    shiftId: "",
    startDate: "",
    endDate: "",
    selectedEmployees: [],
    assignmentType: "Permanent",
  });
  const [departments, setDepartments] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [originalEmployees, setOriginalEmployees] = useState([]);
  const [isSearchingEmployees, setIsSearchingEmployees] = useState(false);
  const rosterSearchInputRef = useRef(null);

  // State for searchable employee dropdown
  const [employeeSearchOpen, setEmployeeSearchOpen] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState("");

  // Store selected employee data to persist even after search is cleared
  const [selectedEmployeeData, setSelectedEmployeeData] = useState(null);
  const [showDeleteShiftModal, setShowDeleteShiftModal] = useState(false);
  const [shiftToDelete, setShiftToDelete] = useState(null);
  const [showDeleteRosterModal, setShowDeleteRosterModal] = useState(false);
  const [rosterToDelete, setRosterToDelete] = useState(null);
  const [shiftFormErrors, setShiftFormErrors] = useState({});
  const [shiftFormSubmitted, setShiftFormSubmitted] = useState(false);
  const [shiftTouched, setShiftTouched] = useState({});
  const [rosterFormErrors, setRosterFormErrors] = useState({});
  const [rosterFormSubmitted, setRosterFormSubmitted] = useState(false);
  const [rosterTouched, setRosterTouched] = useState({});
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  // Get query client for invalidating queries
  const queryClient = useQueryClient();

  // React Query: Delete Shift Mutation
  const deleteShiftMutation = useMutation({
    mutationFn: async (shiftId) => {
      const token = localStorage.getItem("auth_token");
      const url = `${process.env.REACT_APP_API_BASE_URL}/shifts/${shiftId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete shift');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
    },
  });

  // React Query: Delete Roster Mutation
  const deleteRosterMutation = useMutation({
    mutationFn: async (rosterId) => {
      const token = localStorage.getItem("auth_token");
      const url = `${process.env.REACT_APP_API_BASE_URL}/roster-test/${rosterId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete roster assignment');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rosters'] });
    },
  });
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  });
  const [loading, setLoading] = useState(false);

  // Row selection state (separate for each tab)
  const [selectedRosterRows, setSelectedRosterRows] = useState(new Set());
  const [selectedShiftRows, setSelectedShiftRows] = useState(new Set());

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'

  // Map employees by Mongo _id and business employeeId to their display name
  const employeeNameMap = useMemo(() => {
    const map = {};
    employees.forEach((e) => {
      if (e && e.name) {
        if (e.id) map[e.id] = e.name;
        if (e.employeeId) map[e.employeeId] = e.name;
      }
    });
    return map;
  }, [employees]);

  const validateShiftForm = useCallback((data) => {
    const errors = {};
    if (!data.name || !data.name.trim()) {
      errors.name = "Shift name is required.";
    }
    if (!data.shiftType) {
      errors.shiftType = "Shift type is required.";
    }
    if (!data.startTime) {
      errors.startTime = "Start time is required.";
    }
    if (!data.endTime) {
      errors.endTime = "End time is required.";
    } else if (data.startTime && data.startTime === data.endTime) {
      errors.endTime = "End time must be different from start time.";
    }

    if (!data.workingDays || data.workingDays.length === 0) {
      errors.workingDays = "At least one working day must be selected.";
    }
    return errors;
  }, []);

  const validateRosterForm = useCallback((data, editingRoster = null) => {
    const errors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!data.employeeId) {
      errors.employeeId = "Please select an employee.";
    }
    if (!data.shiftId) {
      errors.shiftId = "Please select a shift.";
    }
    if (!data.startDate) {
      errors.startDate = "Start date is required.";
    } else {
      const start = new Date(data.startDate);
      if (!editingRoster && start < today) {
        errors.startDate = "Start date cannot be in the past.";
      } else if (editingRoster && editingRoster.originalStartDate) {
        const originalStart = new Date(editingRoster.originalStartDate);
        originalStart.setHours(0, 0, 0, 0);
        if (start < originalStart) {
          errors.startDate = `Cannot be earlier than ${editingRoster.originalStartDate}.`;
        }
      }
    }
    if (!data.endDate) {
      errors.endDate = "End date is required.";
    } else if (data.startDate) {
      const start = new Date(data.startDate);
      const end = new Date(data.endDate);
      if (end < start) {
        errors.endDate = "End date cannot be before start date.";
      }
    }
    return errors;
  }, []);

  const isShiftFormValid = useMemo(() => {
    const errors = validateShiftForm(shiftFormData);
    return Object.keys(errors).length === 0;
  }, [shiftFormData, validateShiftForm]);

  const isRosterFormValid = useMemo(() => {
    const errors = validateRosterForm(rosterFormData, editingRoster);
    return Object.keys(errors).length === 0;
  }, [rosterFormData, editingRoster, validateRosterForm]);

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Reset sorting when switching tabs
  useEffect(() => {
    setSortField(null);
    setSortOrder('asc');
  }, [viewMode]);

  const getSortedData = (data, type) => {
    if (!sortField || !data || data.length === 0) {
      return data;
    }

    return [...data].sort((a, b) => {
      let aValue, bValue;

      if (type === 'shift') {
        switch (sortField) {
          case 'name':
            aValue = (a.name || '').toLowerCase();
            bValue = (b.name || '').toLowerCase();
            break;
          case 'shiftType':
            aValue = (a.shiftType || '').toLowerCase();
            bValue = (b.shiftType || '').toLowerCase();
            break;
          case 'time': // Start Time
            aValue = (a.startTime || '');
            bValue = (b.startTime || '');
            break;
          case 'workingHours':
            aValue = Number(a.workingHours || a.totalWorkingHours || 0);
            bValue = Number(b.workingHours || b.totalWorkingHours || 0);
            break;
          case 'breakDuration':
            aValue = Number(a.breakDuration || 0);
            bValue = Number(b.breakDuration || 0);
            break;
          case 'status':
            // "Regular" shifts don't have explicit status usually, but maybe 'isActive'
            aValue = a.isActive ? 1 : 0;
            bValue = b.isActive ? 1 : 0;
            break;
          case 'appliedDate':
            aValue = new Date(a.createdAt || 0).getTime();
            bValue = new Date(b.createdAt || 0).getTime();
            break;
          default:
            return 0;
        }
      } else if (type === 'roster') {
        switch (sortField) {
          case 'employee':
            aValue = (a.employeeName || '').toLowerCase();
            bValue = (b.employeeName || '').toLowerCase();
            break;
          case 'shift':
            aValue = (a.shiftName || '').toLowerCase();
            bValue = (b.shiftName || '').toLowerCase();
            break;
          case 'schedule':
            aValue = new Date(a.startDate || 0).getTime();
            bValue = new Date(b.startDate || 0).getTime();
            break;
          case 'status':
            aValue = (a.status || '').toLowerCase();
            bValue = (b.status || '').toLowerCase();
            break;
          case 'appliedDate':
            aValue = new Date(a.createdAt || 0).getTime();
            bValue = new Date(b.createdAt || 0).getTime();
            break;
          default: return 0;
        }
      } else {
        return 0;
      }

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  useEffect(() => {
    // Set user role from localStorage or use default
    const storedRole = localStorage.getItem("user_role") || "super_admin";
    setCurrentUserRole(storedRole);

    loadShifts();
    loadEmployees();
    fetchDepartments();
  }, []);

  // Reset to page 1 when filters or search change
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, searchTerm, filterStartDate, filterEndDate]); // filterDepartment removed - COMMENTED OUT

  // Reset row selections when filters, search, pagination, or tab changes
  useEffect(() => {
    setSelectedRosterRows(new Set());
    setSelectedShiftRows(new Set());
  }, [filterStatus, searchTerm, currentPage, itemsPerPage, viewMode]);

  // Load roster data when pagination, filters, or search change (with debounce for search)
  useEffect(() => {
    const timer = setTimeout(() => {
      loadRoster(currentPage, itemsPerPage);
    }, searchTerm ? 500 : 0); // 500ms debounce for search, immediate for other filters

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, filterStatus, searchTerm, filterStartDate, filterEndDate]); // filterDepartment removed - COMMENTED OUT

  // Debounce employee search for dropdown
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmployeeSearch(employeeSearchTerm);
    }, 300); // 300ms debounce delay for employee search

    return () => clearTimeout(timer);
  }, [employeeSearchTerm]);

  // Handle deep-linking to specific shift or roster
  useEffect(() => {
    if (!id) return;

    const fetchById = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("auth_token");
        let url = "";

        if (initialMode?.includes("shift")) {
          url = `${process.env.REACT_APP_API_BASE_URL}/shifts/${id}`;
          const response = await fetch(url, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const result = await response.json();
          if (result.success && result.data) {
            if (initialMode === "view_shift") {
              setSelectedShiftDetail(result.data);
              setShowShiftDetail(true);
            } else if (initialMode === "edit_shift") {
              setEditingShift(result.data);
              setShiftFormData({
                name: result.data.name || "",
                startTime: result.data.startTime || "",
                endTime: result.data.endTime || "",
                breakDuration: result.data.breakDuration || "",
                workingHours: result.data.workingHours || "",
                description: result.data.description || "",
                isActive: result.data.isActive ?? true,
                shiftType: result.data.shiftType || "Regular",
                workingDays: result.data.workingDays || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
                weeklyHolidays: result.data.weeklyHolidays || ['saturday', 'sunday'],
                saturdayWorking: result.data.saturdayWorking || [],
                department: result.data.department || "All",
                maxEmployees: result.data.maxEmployees || "50",
                graceCheckInTime: result.data.graceCheckInTime || "0",
                graceDays: (result.data.graceDays !== undefined ? result.data.graceDays : "0").toString(),
                graceDaysTime: (result.data.graceDaysTime !== undefined ? result.data.graceDaysTime : "0").toString(),
                graceCheckOutTime: result.data.graceCheckOutTime || "0",
                halfDayRule: result.data.halfDayRule || "0",
                absentRule: result.data.absentRule || "0",
              });
              setShowShiftForm(true);
            }
          }
        } else if (initialMode?.includes("roster")) {
          url = `${process.env.REACT_APP_API_BASE_URL}/roster-test/${id}`;
          const response = await fetch(url, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const result = await response.json();
          if (result.success && result.data) {
            if (initialMode === "view_roster") {
              // Transform to match local expected structure
              const roster = result.data;
              let employeeName = "No Employee";
              if (roster.employeeId && typeof roster.employeeId === 'object') {
                employeeName = `${roster.employeeId.personalInfo?.firstName || ''} ${roster.employeeId.personalInfo?.lastName || ''}`.trim() || roster.employeeId.fullName;
              }
              setSelectedRoster({
                ...roster,
                employeeName,
                shiftName: roster.shiftId?.name || "Unknown Shift",
                shiftGraceCheckInTime: roster.shiftId?.graceCheckInTime ?? 0,
                shiftGraceDays: roster.shiftId?.graceDays ?? 0,
                shiftGraceDaysTime: roster.shiftId?.graceDaysTime ?? 0
              });
              setShowDetails(true);
            } else if (initialMode === "edit_roster") {
              setEditingRoster({
                ...result.data,
                originalStartDate: result.data.startDate
              });
              setRosterFormData({
                employeeId: result.data.employeeId?._id || result.data.employeeId,
                shiftId: result.data.shiftId?._id || result.data.shiftId,
                startDate: result.data.startDate?.split('T')[0] || result.data.startDate,
                endDate: result.data.endDate?.split('T')[0] || result.data.endDate,
                isRecurring: result.data.isRecurring || false,
                recurringDays: result.data.recurringDays || [],
                notes: result.data.notes || "",
                assignmentType: result.data.assignmentType || "Permanent",
              });
              setSelectedEmployeeData(result.data.employeeId);
              setShowRosterForm(true);
            }
          }
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchById();
  }, [id, initialMode]);

  // React Query: Search Employees (only fetch on search, no initial load)
  const { data: searchedEmployeesData, isLoading: isLoadingEmployeeSearch } = useQuery({
    queryKey: ['shift-roster-employees-search', debouncedEmployeeSearch],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('limit', '20');
      const trimmedSearch = debouncedEmployeeSearch?.trim();
      if (trimmedSearch) {
        params.set('search', trimmedSearch);
      }

      const url = `${process.env.REACT_APP_API_BASE_URL}/dropdown-data/employees?${params.toString()}`;

      // Get auth token
      const token = localStorage.getItem('auth_token');
      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });

      if (response.status === 401) {
        return [];
      }

      if (response.ok) {
        const data = await response.json();
        if (data.responseCode === 200) {
          const employeesArr = data.data.employees || [];
          const normalized = employeesArr.map(e => ({
            id: e._id,
            employeeId: e.employeeId,
            name: `${e.personalInfo?.firstName || ''} ${e.personalInfo?.lastName || ''}`.trim(),
            email: e.contactInfo?.personalEmail || '',
            department: e.employmentInfo?.department || '',
          })).filter(e => e.id && e.name);
          return normalized;
        }
      }
      return [];
    },
    staleTime: 0,
  });

  // Get employees to display in dropdown (only searched results, no initial load)
  const dropdownEmployees = searchedEmployeesData || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (employeeSearchOpen && !event.target.closest('.employee-dropdown-container')) {
        setEmployeeSearchOpen(false);
        setEmployeeSearchTerm("");
        setRosterTouched(prev => ({ ...prev, employeeId: true }));
        const errors = validateRosterForm(rosterFormData, editingRoster);
        setRosterFormErrors(prev => ({ ...prev, employeeId: errors.employeeId }));
      }
    };

    if (employeeSearchOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [employeeSearchOpen]);

  // Close dropdown when form closes (but preserve selectedEmployeeData)
  useEffect(() => {
    if (!showRosterForm) {
      setEmployeeSearchOpen(false);
      setEmployeeSearchTerm("");
      // Don't clear selectedEmployeeData here - it should persist until form is reset
    }
  }, [showRosterForm]);

  // When employees are (re)loaded, backfill missing/undefined employee names in roster
  useEffect(() => {
    if (!employees || employees.length === 0 || !roster || roster.length === 0) return;
    const updated = roster.map((entry) => {
      const needsPatch =
        !entry.employeeName ||
        entry.employeeName === "undefined undefined" ||
        entry.employeeName === "Employee " ||
        /^(Employee\s)/.test(entry.employeeName);
      if (!needsPatch) return entry;
      const name = employeeNameMap[entry.employeeId];
      if (name) {
        return { ...entry, employeeName: name };
      }
      return entry;
    });
    // Only update if there is any change
    const changed = updated.some((e, i) => e.employeeName !== roster[i].employeeName);
    if (changed) setRoster(updated);
  }, [employees, employeeNameMap]);

  const loadShifts = async () => {
    try {
      // Use the test endpoint to get real database data (no auth required)
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/shifts`,
        {
          method: "GET",
          headers: buildJsonHeaders(),
        }
      );

      if (response.ok) {
        const result = await response.json();
        // Use the real database data from test endpoint
        const transformedShifts = result.data.shifts.map((shift) => ({
          id: shift.id,
          name: shift.name,
          startTime: shift.startTime,
          endTime: shift.endTime,
          breakDuration: shift.breakDuration,
          workingHours: shift.workingHours,
          description: shift.description,
          isActive: shift.isActive,
          shiftType: shift.shiftType,
          department: shift.department,
          maxEmployees: shift.maxEmployees,
          currentEmployees: shift.currentEmployees,
          createdAt: shift.createdAt,
          updatedAt: shift.updatedAt || shift.updateAt || shift.updated_at || null,
          createdBy: shift.createdBy,
          updatedBy: shift.updatedBy,
          isFlexible: shift.isFlexible,
          graceCheckInTime: shift.graceCheckInTime,
          graceDays: shift.graceDays !== undefined ? shift.graceDays : 0,
          graceDaysTime: shift.graceDaysTime !== undefined ? shift.graceDaysTime : 0,
          graceCheckOutTime: shift.graceCheckOutTime,
          halfDayRule: shift.halfDayRule,
          absentRule: shift.absentRule,
          workingDays: shift.workingDays || [],
          weeklyHolidays: shift.weeklyHolidays || [],
          saturdayWorking: shift.saturdayWorking || [],
        }));

        setShifts(transformedShifts);
      } else {
        setShifts([]);
      }
    } catch (error) {
      setShifts([]);
    }
  };

  // Helper to build headers with optional auth
  const buildJsonHeaders = () => {
    const headers = { "Content-Type": "application/json" };
    const token = localStorage.getItem("auth_token");
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
  };

  const loadEmployees = async () => {
    // This function is kept for backward compatibility but employees are now loaded via React Query
    // The employeesData from useQuery hook will be used instead
  };

  // Calculate status based on start and end dates
  const calculateStatus = (startDate, endDate) => {
    if (!startDate || !endDate) return "Upcoming";

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to compare dates only

    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    // If end date is in the past, status is Completed
    if (end < today) {
      return "Completed";
    }

    // If today is between start date and end date (inclusive), status is Active
    if (start <= today && today <= end) {
      return "Active";
    }

    // If start date is in the future, status is Upcoming
    if (start > today) {
      return "Upcoming";
    }

    // Default to Upcoming
    return "Upcoming";
  };

  // Calculate total working hours (gross) from start and end time (HH:mm format)
  const calculateWorkingHours = (startTime, endTime, breakDuration = 0) => {
    if (!startTime || !endTime || startTime === "N/A" || endTime === "N/A") {
      return null;
    }

    try {
      // Parse time strings (format: "HH:mm" or "HH:MM")
      const parseTime = (timeStr) => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 60 + minutes; // Convert to total minutes
      };

      const startMinutes = parseTime(startTime);
      let endMinutes = parseTime(endTime);

      // Handle overnight shifts (if end time is earlier than start time, it's next day)
      if (endMinutes <= startMinutes) {
        endMinutes += 24 * 60; // Add 24 hours
      }

      const totalMinutes = endMinutes - startMinutes;
      const hours = totalMinutes / 60;

      // Round to 1 decimal place
      return Math.round(hours * 10) / 10;
    } catch (error) {
      return null;
    }
  };

  const loadRoster = async (page = currentPage, limit = itemsPerPage) => {
    setLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      // Add status filter
      if (filterStatus && filterStatus !== "all") {
        params.append("status", filterStatus);
      }

      // Add search term to backend
      if (searchTerm && searchTerm.trim()) {
        params.append("search", searchTerm.trim());
      }

      if (filterStartDate) {
        params.append("startDate", filterStartDate);
      }

      if (filterEndDate) {
        params.append("endDate", filterEndDate);
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/roster-test?${params.toString()}`,
        {
          method: "GET",
          headers: buildJsonHeaders(),
        }
      );

      if (response.ok) {
        const result = await response.json();
        const rosters = result.data?.rosters || [];
        const paginationData = result.data?.pagination || {};

        // Update pagination state
        const currentPageNum = paginationData.currentPage || page;
        const totalPagesNum = paginationData.totalPages || 1;
        const itemsPerPageValue = paginationData.itemsPerPage || limit;

        setPagination({
          currentPage: currentPageNum,
          totalPages: totalPagesNum,
          totalItems: paginationData.totalItems || 0,
          itemsPerPage: itemsPerPageValue,
          hasNextPage: currentPageNum < totalPagesNum,
          hasPrevPage: currentPageNum > 1,
          limit: itemsPerPageValue
        });

        // Transform rosters
        const transformedRosters = rosters.map((roster) => {
          // Fix employee name extraction
          let employeeName = "No Employee";
          let department = "Unknown";

          if (roster.employeeId) {
            // Handle different employee data structures
            if (
              roster.employeeId.personalInfo &&
              roster.employeeId.personalInfo.firstName &&
              roster.employeeId.personalInfo.lastName
            ) {
              employeeName = `${roster.employeeId.personalInfo.firstName} ${roster.employeeId.personalInfo.lastName}`;
            } else if (
              roster.employeeId.fullName &&
              roster.employeeId.fullName !== "undefined undefined"
            ) {
              employeeName = roster.employeeId.fullName;
            } else if (roster.employeeId.employeeId) {
              employeeName = `Employee ${roster.employeeId.employeeId}`;
            }

            department = roster.employeeId.department || "Unknown";

            // If backend returns undefined name, attempt to patch from local map
            if (
              !employeeName ||
              employeeName === "undefined undefined" ||
              employeeName === "Employee "
            ) {
              const byMongo = employeeNameMap[roster.employeeId._id];
              const byCode = employeeNameMap[roster.employeeId.employeeId];
              if (byMongo) employeeName = byMongo;
              else if (byCode) employeeName = byCode;
            }
          }

          // Recalculate status based on current dates
          // This ensures status is always accurate based on today's date
          const recalculatedStatus = calculateStatus(roster.startDate, roster.endDate);

          // If the calculated status differs from stored status, especially if it should be Completed,
          // we'll update it in the background (async, non-blocking)
          if (recalculatedStatus === "Completed" && roster.status !== "Completed") {
            // Update status in database asynchronously (don't block UI)
            const token = localStorage.getItem("auth_token");
            fetch(
              `${process.env.REACT_APP_API_BASE_URL}/roster-test/${roster.id}`,
              {
                method: "PUT",
                headers: buildJsonHeaders(),
                body: JSON.stringify({
                  status: "Completed",
                }),
              }
            ).catch((err) => {
              // Silently fail - we'll retry on next load
            });
          }

          // Extract business employeeId from populated employee object
          let businessEmployeeId = null;
          let employeeEmail = null;
          if (roster.employeeId) {
            // Check if employeeId is populated (object) or just an ObjectId
            if (typeof roster.employeeId === 'object' && roster.employeeId.employeeId) {
              businessEmployeeId = roster.employeeId.employeeId;
            } else if (typeof roster.employeeId === 'object' && roster.employeeId.employmentInfo?.employeeId) {
              businessEmployeeId = roster.employeeId.employmentInfo.employeeId;
            }

            // Extract email from employee object
            if (typeof roster.employeeId === 'object') {
              employeeEmail = roster.employeeId.contactInfo?.personalEmail ||
                roster.employeeId.personalInfo?.email ||
                null;
            }
          }

          return {
            id: roster.id,
            employeeId: roster.employeeId ? (roster.employeeId._id || roster.employeeId) : null,
            businessEmployeeId: businessEmployeeId, // Store business employeeId for updates
            employeeEmail: employeeEmail, // Store employee email for display
            employeeName: employeeName,
            department: department,
            shiftId: roster.shiftId ? roster.shiftId._id : null,
            shiftName: roster.shiftId ? roster.shiftId.name : "Unknown Shift",
            shiftGraceCheckInTime: roster.shiftId ? (roster.shiftId.graceCheckInTime || 0) : 0,
            shiftGraceDays: roster.shiftId ? (roster.shiftId.graceDays || 0) : 0,
            shiftGraceDaysTime: roster.shiftId ? (roster.shiftId.graceDaysTime || 0) : 0,
            startDate: roster.startDate,
            endDate: roster.endDate,
            isRecurring: roster.isRecurring,
            recurringDays: roster.recurringDays,
            notes: roster.notes,
            status: recalculatedStatus, // Use recalculated status
            assignmentType: roster.assignmentType,
            priority: roster.priority,
            createdAt: roster.createdAt,
          };
        });
        setRoster(transformedRosters);
        // Backend filtering is already applied, so filteredRoster is the same as roster
        setFilteredRoster(transformedRosters);
      } else {
        setRoster([]);
        setFilteredRoster([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: limit,
          hasNextPage: false,
          hasPrevPage: false,
          limit: limit
        });
      }
    } catch (error) {
      setRoster([]);
      setFilteredRoster([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: limit,
        hasNextPage: false,
        hasPrevPage: false,
        limit: limit
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        setDepartments([]);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/hr/departments`,
        {
          method: "GET",
          headers: buildJsonHeaders(),
        }
      );

      if (response.status === 401) {
        setDepartments([]);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        // Support multiple shapes: {departments: [...]}, {data:{departments:[...]}}, or direct array
        const raw = Array.isArray(data)
          ? data
          : Array.isArray(data?.departments)
            ? data.departments
            : Array.isArray(data?.data?.departments)
              ? data.data.departments
              : Array.isArray(data?.data)
                ? data.data
                : [];
        const normalized = raw
          .map((d) => (typeof d === "string" ? d : d?.name || d?.departmentName))
          .filter(Boolean);
        setDepartments(normalized);
      } else {
        setDepartments([]);
      }
    } catch (error) {
      setDepartments([]);
    }
  };

  const weekDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
  ];

  // Client-side filtering removed - all filtering is now done on the backend

  const handleShiftSaturdayToggle = (saturdayNumber) => {
    setShiftFormData(prev => {
      const currentSaturdays = [...(prev.saturdayWorking || [])];
      const index = currentSaturdays.indexOf(saturdayNumber);

      if (index > -1) {
        currentSaturdays.splice(index, 1);
      } else {
        currentSaturdays.push(saturdayNumber);
      }

      return { ...prev, saturdayWorking: currentSaturdays };
    });
  };

  const handleShiftInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    if (name === "name") {
      newValue = sanitizeAlphaNumericCommaAllowedSymbols(newValue, TEXT_LIMITS.shiftName);
    } else if (name === "description") {
      newValue = sanitizeAlphaNumericCommaAllowedSymbols(newValue, TEXT_LIMITS.description);
    } else if (name === "breakDuration") {
      newValue = sanitizePositiveNumber(newValue, NUMBER_LIMITS.breakDuration);
    }

    const updatedData = { ...shiftFormData, [name]: newValue };

    if (name === "startTime" || name === "endTime" || name === "breakDuration") {
      if (updatedData.startTime && updatedData.endTime) {
        const start = new Date(`2024-01-01T${updatedData.startTime}:00`);
        const end = new Date(`2024-01-01T${updatedData.endTime}:00`);

        if (end < start) {
          end.setDate(end.getDate() + 1);
        }

        const diffMs = end - start;
        const diffHours = diffMs / (1000 * 60 * 60);
        updatedData.workingHours = diffHours.toFixed(1);
      }
    }

    setShiftFormData(updatedData);

    // Clear error when typing
    if (shiftFormErrors[name]) {
      setShiftFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleShiftDayToggle = (day, field) => {
    const otherField = field === 'workingDays' ? 'weeklyHolidays' : 'workingDays';

    setShiftFormData(prev => {
      const currentDays = [...(prev[field] || [])];
      const otherDays = [...(prev[otherField] || [])];

      const index = currentDays.indexOf(day);
      let newCurrentDays = [...currentDays];
      let newOtherDays = [...otherDays];

      if (index > -1) {
        // Deselecting the day
        newCurrentDays.splice(index, 1);
      } else {
        // Selecting the day
        newCurrentDays.push(day);
        // Remove from other field to ensure mutual exclusivity
        const otherIndex = otherDays.indexOf(day);
        if (otherIndex > -1) {
          newOtherDays.splice(otherIndex, 1);
        }
      }

      return {
        ...prev,
        [field]: newCurrentDays,
        [otherField]: newOtherDays
      };
    });

    if (shiftFormErrors[field]) {
      setShiftFormErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleShiftBlur = (e) => {
    const { name, value } = e.target;
    setShiftTouched(prev => ({ ...prev, [name]: true }));
    const errors = validateShiftForm({ ...shiftFormData, [name]: value });
    setShiftFormErrors(prev => ({ ...prev, [name]: errors[name] }));
  };

  const handleRosterInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    if (name === "notes") {
      newValue = sanitizeAlphaNumericCommaHyphen(newValue, 500);
    }

    const updatedData = {
      ...rosterFormData,
      [name]: newValue,
    };

    setRosterFormData(updatedData);

    // Clear error when typing
    if (rosterFormErrors[name]) {
      setRosterFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleRosterBlur = (e) => {
    const { name, value } = e.target;
    setRosterTouched(prev => ({ ...prev, [name]: true }));
    const errors = validateRosterForm({ ...rosterFormData, [name]: value }, editingRoster);
    setRosterFormErrors(prev => ({ ...prev, [name]: errors[name] }));
  };

  const handleRecurringDayChange = (day) => {
    setRosterFormData((prev) => ({
      ...prev,
      recurringDays: prev.recurringDays.includes(day)
        ? prev.recurringDays.filter((d) => d !== day)
        : [...prev.recurringDays, day],
    }));
  };

  const handleBulkInputChange = (e) => {
    const { name, value } = e.target;
    setBulkFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBulkEmployeeToggle = (employeeId) => {
    setBulkFormData((prev) => ({
      ...prev,
      selectedEmployees: prev.selectedEmployees.includes(employeeId)
        ? prev.selectedEmployees.filter((id) => id !== employeeId)
        : [...prev.selectedEmployees, employeeId],
    }));
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();

    if (bulkFormData.selectedEmployees.length === 0) {
      showError("Please select at least one employee");
      return;
    }

    if (!bulkFormData.shiftId) {
      showError("Please select a shift");
      return;
    }

    if (!bulkFormData.startDate || !bulkFormData.endDate) {
      showError("Please select start and end dates");
      return;
    }

    const startDate = new Date(bulkFormData.startDate);
    const endDate = new Date(bulkFormData.endDate);
    if (endDate < startDate) {
      showError("End date must be on or after start date");
      return;
    }

    try {
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (const employeeMongoId of bulkFormData.selectedEmployees) {
        try {
          // Find the employee by MongoDB _id to get their employeeId field
          const employee = employees.find((emp) => emp.id === employeeMongoId);
          if (!employee) {
            errors.push(`Employee not found for ID: ${employeeMongoId}`);
            errorCount++;
            continue;
          }

          const employeeId = employee.employeeId; // Use the employeeId field (like "EMP403407675")

          // Check for overlapping assignments
          const overlapping = checkOverlappingAssignments(
            employeeMongoId,
            bulkFormData.startDate,
            bulkFormData.endDate
          );
          if (overlapping.length > 0) {
            errors.push(
              `${employee.fullName || employee.firstName
              }: Already has assignments for this period`
            );
            errorCount++;
            continue;
          }

          // Calculate status based on dates
          const calculatedStatus = calculateStatus(
            bulkFormData.startDate,
            bulkFormData.endDate
          );

          const rosterData = {
            employeeId: employeeId, // Use the employeeId field, not MongoDB _id
            employeeName: employee.fullName || employee.firstName || employee.name,
            shiftId: bulkFormData.shiftId,
            startDate: bulkFormData.startDate,
            endDate: bulkFormData.endDate,
            isRecurring: false,
            recurringDays: [],
            notes: `Bulk assignment - ${bulkFormData.department
              ? bulkFormData.department + " department"
              : "All departments"
              }`,
            assignmentType: bulkFormData.assignmentType || "Permanent",
            priority: "Medium",
            workLocation: "",
            isRemote: false,
            specialRequirements: [],
            status: calculatedStatus, // Set status based on date calculation
          };

          const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/roster-test`,
            {
              method: "POST",
              headers: buildJsonHeaders(),
              body: JSON.stringify(rosterData),
            }
          );

          if (response.ok) {
            successCount++;
          } else {
            const errorResult = await response.json();
            const employee = employees.find(
              (emp) => emp.id === employeeMongoId
            );
            errors.push(
              `${employee?.fullName || employee?.firstName}: ${errorResult.message || "Unknown error"
              }`
            );
            errorCount++;
          }
        } catch (error) {
          const employee = employees.find((emp) => emp.id === employeeMongoId);
          errors.push(
            `${employee?.fullName || employee?.firstName}: ${error.message}`
          );
          errorCount++;
        }
      }

      // Show results
      if (successCount > 0) {
        showSuccess(`Successfully assigned ${successCount} employee(s) to shift`);
        await loadRoster();
      }

      if (errorCount > 0) {
        showError(
          `Failed to assign ${errorCount} employee(s):\n\n${errors.join(
            "\n"
          )}`
        );
      }

      // Reset bulk form
      setBulkFormData({
        department: "",
        shiftId: "",
        startDate: "",
        endDate: "",
        selectedEmployees: [],
        assignmentType: "Permanent",
      });
    } catch (error) {
      showError(`Bulk assignment failed: ${error.message}`);
    }
  };

  const handleSubmitShift = async (e) => {
    e.preventDefault();
    setShiftFormSubmitted(true);

    const errors = validateShiftForm(shiftFormData);
    setShiftFormErrors(errors);

    // Mark all fields as touched
    const touchedFields = Object.keys(shiftFormData).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setShiftTouched(touchedFields);

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      // Calculate working hours if not already set
      let workingHours = parseFloat(shiftFormData.workingHours) || 0;
      if (!workingHours && shiftFormData.startTime && shiftFormData.endTime) {
        const start = new Date(`2024-01-01T${shiftFormData.startTime}:00`);
        const end = new Date(`2024-01-01T${shiftFormData.endTime}:00`);
        if (end < start) {
          end.setDate(end.getDate() + 1);
        }
        const diffMs = end - start;
        const diffHours = diffMs / (1000 * 60 * 60);
        workingHours = diffHours;
      }

      const parsedBreakDuration = parseInt(shiftFormData.breakDuration || "0", 10) || 0;
      const totalHours = workingHours || 0;

      const shiftData = {
        name: shiftFormData.name.trim(),
        startTime: shiftFormData.startTime,
        endTime: shiftFormData.endTime,
        breakDuration: parsedBreakDuration,
        workingHours: workingHours,
        totalWorkingHours: workingHours,
        description: shiftFormData.description || "",
        shiftType: shiftFormData.shiftType,
        workingDays: shiftFormData.workingDays || [],
        weeklyHolidays: shiftFormData.weeklyHolidays || [],
        saturdayWorking: shiftFormData.saturdayWorking || [],
        isActive: shiftFormData.isActive !== undefined ? shiftFormData.isActive : true,
        isFlexible: shiftFormData.isFlexible || false,
        graceCheckInTime: parseInt(shiftFormData.graceCheckInTime) || 0,
        graceDays: parseInt(shiftFormData.graceDays) || 0,
        graceDaysTime: parseInt(shiftFormData.graceDaysTime) || 0,
        graceCheckOutTime: parseInt(shiftFormData.graceCheckOutTime) || 0,
        halfDayRule: parseFloat(shiftFormData.halfDayRule) || 0,
        absentRule: parseFloat(shiftFormData.absentRule) || 0,
      };
      const url = editingShift
        ? `${process.env.REACT_APP_API_BASE_URL}/shifts/${editingShift.id}`
        : `${process.env.REACT_APP_API_BASE_URL}/shifts`;

      const method = editingShift ? "PUT" : "POST";
      const token = localStorage.getItem("auth_token");

      const response = await fetch(url, {
        method: method,
        headers: buildJsonHeaders(),
        body: JSON.stringify(shiftData),
      });

      if (!response.ok) {
        let errorResult;
        try {
          errorResult = await response.json();
        } catch (parseError) {
          errorResult = { message: "Unknown error", status: response.status };
        }
        const errorMessage =
          errorResult.message || errorResult.error || "Unknown error";
        throw new Error(
          `HTTP error! status: ${response.status}, message: ${errorMessage}`
        );
      }

      const result = await response.json();
      if (result.success === true) {
        showSuccess(`Shift ${editingShift ? "updated" : "created"} successfully!`);
        // Reload shifts to show the updated list
        await loadShifts();
        setShowShiftForm(false);
        setEditingShift(null);
        if (onClose) onClose();
      } else {
        showError(
          "Failed to submit shift: " + (result.message || "Unknown error")
        );
      }
    } catch (error) {
      const errorMessage = error.message || "Unknown error occurred";
      showError(`Failed to submit shift: ${errorMessage}`);
    }
  };

  const handleSubmitRoster = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setRosterFormSubmitted(true);
    const errors = validateRosterForm(rosterFormData, editingRoster);
    setRosterFormErrors(errors);

    // Mark all fields as touched
    const touchedFields = Object.keys(rosterFormData).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setRosterTouched(touchedFields);

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      const startDate = new Date(rosterFormData.startDate);
      const endDate = new Date(rosterFormData.endDate);

      if (endDate < startDate) {
        showError("End date must be on or after start date");
        return;
      }

      // Validate employee selection
      if (!rosterFormData.employeeId) {
        showError("Please select an employee");
        return;
      }

      // Get employee data for employeeName and business employeeId
      // rosterFormData.employeeId is the MongoDB _id (stored for form state)
      // But backend expects the business employeeId (like "EMP403407675")
      let selectedEmployee = selectedEmployeeData;
      let businessEmployeeId;

      // If we don't have selectedEmployeeData, try to get from employees state
      if (!selectedEmployee || selectedEmployee.id !== rosterFormData.employeeId) {
        const foundEmployee = employees.find(
          (emp) => emp.id === rosterFormData.employeeId
        );
        if (foundEmployee) {
          selectedEmployee = foundEmployee;
        }
      }

      // Get the business employeeId to send to backend
      // Backend expects business employeeId (like "EMP403407675"), not MongoDB _id
      if (selectedEmployee && selectedEmployee.employeeId) {
        businessEmployeeId = selectedEmployee.employeeId;
      } else {
        // Fallback: if we don't have the business employeeId, use the MongoDB _id
        // This should rarely happen, but provides a fallback
        businessEmployeeId = rosterFormData.employeeId;
      }
      // Check for overlapping assignments (only if not editing)
      // Note: checkOverlappingAssignments uses MongoDB _id, so use rosterFormData.employeeId
      if (!editingRoster) {
        const overlapping = checkOverlappingAssignments(
          rosterFormData.employeeId,
          rosterFormData.startDate,
          rosterFormData.endDate
        );
        if (overlapping.length > 0) {
          const overlappingDetails = overlapping
            .map(
              (assignment) =>
                `${assignment.shiftName} from ${new Date(
                  assignment.startDate
                ).toLocaleDateString()} to ${new Date(
                  assignment.endDate
                ).toLocaleDateString()}`
            )
            .join(", ");
          showWarning(
            `Employee already has roster assignments for this period. Please choose a different date range. Existing assignments: ${overlappingDetails}`
          );
          return;
        }
      }

      // Calculate status based on dates
      const calculatedStatus = calculateStatus(
        rosterFormData.startDate,
        rosterFormData.endDate
      );

      const rosterData = {
        employeeId: businessEmployeeId, // Backend expects business employeeId (like "EMP403407675")
        employeeName: selectedEmployee?.name || selectedEmployeeData?.name || "Unknown Employee",
        shiftId: rosterFormData.shiftId,
        startDate: rosterFormData.startDate,
        endDate: rosterFormData.endDate,
        isRecurring: rosterFormData.isRecurring,
        recurringDays: rosterFormData.isRecurring
          ? rosterFormData.recurringDays
          : [],
        recurringPattern: rosterFormData.isRecurring ? "weekly" : undefined,
        notes: rosterFormData.notes,
        assignmentType: rosterFormData.assignmentType || "Permanent",
        priority: "Medium",
        workLocation: "",
        isRemote: false,
        specialRequirements: [],
        status: calculatedStatus, // Set status based on date calculation
      };

      const url = editingRoster
        ? `${process.env.REACT_APP_API_BASE_URL}/roster-test/${editingRoster.id}`
        : `${process.env.REACT_APP_API_BASE_URL}/roster-test`;

      const method = editingRoster ? "PUT" : "POST";

      const authToken = localStorage.getItem("auth_token");

      // Prepare headers
      const headers = {
        "Content-Type": "application/json",
      };

      // Add authorization header only if token exists
      if (authToken) {
        headers["Authorization"] = `Bearer ${authToken}`;
      }

      const response = await fetch(url, {
        method: method,
        headers: headers,
        body: JSON.stringify(rosterData),
      });

      if (!response.ok) {
        let errorResult;
        try {
          errorResult = await response.json();
        } catch (parseError) {
          errorResult = { message: "Unknown error", status: response.status };
        }

        // Handle specific error cases
        if (response.status === 409) {
          throw new Error(
            `Employee has overlapping roster assignments for this period. Please choose a different date range or check existing assignments.`
          );
        } else if (response.status === 400) {
          const errorMessage = errorResult.message || "Please check all required fields are filled correctly.";
          // Check if it's an overlapping assignment error
          if (errorMessage.toLowerCase().includes('overlapping')) {
            throw new Error(errorMessage);
          } else {
            throw new Error(`Invalid request: ${errorMessage}`);
          }
        } else {
          throw new Error(
            `HTTP error! status: ${response.status}, message: ${errorResult.message || "Unknown error"
            }`
          );
        }
      }

      const result = await response.json();

      if (result.success === true) {
        showSuccess(
          `Roster assignment ${editingRoster ? "updated" : "created"} successfully!`
        );
        await loadRoster();
        resetRosterForm();
        if (onClose) onClose();
      } else {
        const errorMessage = result.message || "Unknown error";
        // Check if it's an overlapping assignment error
        if (errorMessage.toLowerCase().includes('overlapping')) {
          showWarning("Employee has overlapping roster assignments for this period. Please choose a different date range or check existing assignments.");
        } else {
          showError("Failed to submit roster: " + errorMessage);
        }
      }
    } catch (error) {
      const errorMessage = error.message || "Unknown error";
      // Check if it's an overlapping assignment error
      if (errorMessage.toLowerCase().includes('overlapping')) {
        showWarning("Employee has overlapping roster assignments for this period. Please choose a different date range or check existing assignments.");
      } else {
        showError(`Failed to submit roster: ${errorMessage}`);
      }
    }
  };

  const resetShiftForm = () => {
    setShiftFormData({
      name: "",
      startTime: "",
      endTime: "",
      breakDuration: "",
      workingHours: "",
      description: "",
      isActive: true,
      shiftType: "Regular",
      workingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      weeklyHolidays: ['saturday', 'sunday'],
      saturdayWorking: [],
      isFlexible: false,
      graceCheckInTime: "0",
      graceDays: "0",
      graceDaysTime: "0",
      graceCheckOutTime: "0",
      halfDayRule: "0",
      absentRule: "0",
    });
    setShowShiftForm(false);
    setEditingShift(null);
    setShiftFormErrors({});
    setShiftFormSubmitted(false);
    setShiftTouched({});
  };

  const handleNewShift = () => {
    setShiftFormData({
      name: "",
      startTime: "",
      endTime: "",
      breakDuration: "",
      workingHours: "",
      description: "",
      isActive: true,
      shiftType: "Regular",
      workingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      weeklyHolidays: ['saturday', 'sunday'],
      saturdayWorking: [],
      isFlexible: false,
      graceCheckInTime: "0",
      graceDays: "0",
      graceDaysTime: "0",
      graceCheckOutTime: "0",
      halfDayRule: "0",
      absentRule: "0",
    });
    setEditingShift(null);
    setShowShiftForm(true);
    setShiftFormErrors({});
    setShiftFormSubmitted(false);
    setShiftTouched({});
  };

  // Check if user has permission for manual assignment
  const canAssignManually = () => {
    const hasPermission =
      currentUserRole === "super_admin" ||
      currentUserRole === "hr_manager" ||
      currentUserRole === "hr_executive" ||
      !currentUserRole; // Allow access if no role is set

    return hasPermission;
  };

  // Check for overlapping assignments
  const checkOverlappingAssignments = (employeeId, startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    return roster.filter((assignment) => {
      if (assignment.employeeId !== employeeId) return false;

      const assignmentStart = new Date(assignment.startDate);
      const assignmentEnd = new Date(assignment.endDate);

      // Check for overlap
      return start <= assignmentEnd && end >= assignmentStart;
    });
  };

  const resetRosterForm = () => {
    setRosterFormData({
      employeeId: "",
      shiftId: "",
      startDate: "",
      endDate: "",
      isRecurring: false,
      recurringDays: [],
      notes: "",
      assignmentType: "Permanent",
    });
    setSelectedEmployeeData(null);
    setShowRosterForm(false);
    setFormSubmitted(false);
    setRosterFormSubmitted(false);
    setRosterFormErrors({});
    setRosterTouched({});
    setEditingRoster(null);
    setEmployeeSearchOpen(false);
    setEmployeeSearchTerm("");
  };

  const handleEditShift = (shift) => {
    // Calculate working hours if not present
    let calculatedWorkingHours = shift.totalWorkingHours || shift.workingHours || "";
    if (!calculatedWorkingHours && shift.startTime && shift.endTime) {
      const start = new Date(`2024-01-01T${shift.startTime}:00`);
      const end = new Date(`2024-01-01T${shift.endTime}:00`);
      if (end < start) {
        end.setDate(end.getDate() + 1);
      }
      const diffMs = end - start;
      const diffHours = diffMs / (1000 * 60 * 60);
      calculatedWorkingHours = Math.max(0, diffHours).toFixed(1);
    }

    setShiftFormData({
      name: shift.name || "",
      startTime: shift.startTime || "",
      endTime: shift.endTime || "",
      breakDuration: shift.breakDuration
        ? shift.breakDuration.toString()
        : "60",
      workingHours: calculatedWorkingHours.toString(),
      description: shift.description || "",
      isActive: shift.isActive !== undefined ? shift.isActive : true,
      shiftType: shift.shiftType || "Regular",
      workingDays: shift.workingDays || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      weeklyHolidays: shift.weeklyHolidays || ['saturday', 'sunday'],
      saturdayWorking: shift.saturdayWorking || [],
      isFlexible: shift.isFlexible ?? false,
      graceCheckInTime: (shift.graceCheckInTime || 0).toString(),
      graceDays: (shift.graceDays || 0).toString(),
      graceDaysTime: (shift.graceDaysTime || 0).toString(),
      graceCheckOutTime: (shift.graceCheckOutTime || 0).toString(),
      halfDayRule: (shift.halfDayRule || 0).toString(),
      absentRule: (shift.absentRule || 0).toString(),
    });
    setEditingShift(shift);
    setShowShiftForm(true);
  };

  const handleViewShiftDetail = (shift) => {
    setSelectedShiftDetail(shift);
    setShowShiftDetail(true);
  };

  const handleEditRoster = async (rosterEntry) => {
    // Helper function to format date for HTML date input (YYYY-MM-DD)
    const formatDateForInput = (dateString) => {
      if (!dateString) return "";
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "";
        // Format as YYYY-MM-DD
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      } catch (error) {
        // If dateString is already in YYYY-MM-DD format, return as is
        if (typeof dateString === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
          return dateString;
        }
        return "";
      }
    };

    // Store original start date for validation when editing
    const originalStartDate = formatDateForInput(rosterEntry.startDate);

    // rosterEntry.employeeId is the MongoDB _id from the database
    // We need to fetch the full employee data to get the business employeeId and name
    const employeeMongoId = rosterEntry.employeeId;

    setRosterFormData({
      employeeId: employeeMongoId, // Store MongoDB _id for form state
      shiftId: rosterEntry.shiftId,
      startDate: originalStartDate,
      endDate: formatDateForInput(rosterEntry.endDate),
      isRecurring: rosterEntry.isRecurring,
      recurringDays: rosterEntry.recurringDays || [],
      notes: rosterEntry.notes || "",
      assignmentType: rosterEntry.assignmentType || "Permanent",
    });

    // Fetch employee data for display and to get business employeeId
    try {
      if (employeeMongoId) {
        // First, check if we already have the business employeeId stored in the roster entry
        if (rosterEntry.businessEmployeeId) {
          // We have the business employeeId - create employee object for display
          setSelectedEmployeeData({
            id: employeeMongoId,
            employeeId: rosterEntry.businessEmployeeId,
            name: rosterEntry.employeeName || "Unknown Employee",
            email: '',
            department: rosterEntry.department || '',
          });
        } else if (rosterEntry.employeeId && typeof rosterEntry.employeeId === 'object' && rosterEntry.employeeId.employeeId) {
          // Employee data is already populated in the roster entry (from API response)
          const emp = rosterEntry.employeeId;
          setSelectedEmployeeData({
            id: emp._id || employeeMongoId,
            employeeId: emp.employeeId || emp.employmentInfo?.employeeId,
            name: `${emp.personalInfo?.firstName || ''} ${emp.personalInfo?.lastName || ''}`.trim(),
            email: emp.contactInfo?.personalEmail || '',
            department: emp.employmentInfo?.department || '',
          });
        } else if (rosterEntry.employeeName) {
          // We have the name but need to fetch to get business employeeId
          // Try to fetch from API using the employee name or MongoDB _id
          const searchTerm = rosterEntry.employeeName.split(' ')[0] || employeeMongoId;
          const url = `${process.env.REACT_APP_API_BASE_URL}/dropdown-data/employees?search=${encodeURIComponent(searchTerm)}`;

          // Get auth token
          const token = localStorage.getItem('auth_token');
          const headers = {
            'Content-Type': 'application/json'
          };

          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }

          const response = await fetch(url, {
            method: 'GET',
            headers: headers
          });
          if (response.ok) {
            const data = await response.json();
            if (data.responseCode === 200) {
              const employeesArr = data.data.employees || [];
              const normalized = employeesArr.map(e => ({
                id: e._id,
                employeeId: e.employeeId,
                name: `${e.personalInfo?.firstName || ''} ${e.personalInfo?.lastName || ''}`.trim(),
                email: e.contactInfo?.personalEmail || '',
                department: e.employmentInfo?.department || '',
              })).filter(e => e.id && e.name);
              // Find by MongoDB _id first, then by name match
              const employee = normalized.find((emp) =>
                emp.id === employeeMongoId ||
                emp.name.toLowerCase() === rosterEntry.employeeName.toLowerCase()
              );
              if (employee) {
                setSelectedEmployeeData(employee);
              } else {
                // Fallback: create minimal employee object with what we have
                setSelectedEmployeeData({
                  id: employeeMongoId,
                  employeeId: employeeMongoId, // Fallback to MongoDB _id if business ID not found
                  name: rosterEntry.employeeName,
                });
              }
            }
          }
        } else {
          // Fetch by MongoDB _id directly - need to search all employees
          // Since the search endpoint searches by name/email/employeeId, we'll try a different approach
          // Try fetching from the roster detail endpoint which should have populated data
          try {
            const detailUrl = `${process.env.REACT_APP_API_BASE_URL}/roster-test/${rosterEntry.id}`;
            const detailResponse = await fetch(detailUrl, {
              method: 'GET',
              headers: buildJsonHeaders()
            });
            if (detailResponse.ok) {
              const detailData = await detailResponse.json();
              if (detailData.data && detailData.data.employeeId) {
                const emp = detailData.data.employeeId;
                setSelectedEmployeeData({
                  id: emp._id || employeeMongoId,
                  employeeId: emp.employeeId || emp.employmentInfo?.employeeId,
                  name: `${emp.personalInfo?.firstName || ''} ${emp.personalInfo?.lastName || ''}`.trim() || rosterEntry.employeeName,
                  email: emp.contactInfo?.personalEmail || '',
                  department: emp.employmentInfo?.department || '',
                });
              }
            }
          } catch (detailError) {
          }
        }
      }
    } catch (error) {
    }

    // Store the original start date in editingRoster for validation
    setEditingRoster({
      ...rosterEntry,
      originalStartDate: originalStartDate, // Store formatted original start date
    });
    setShowRosterForm(true);
  };

  const handleDeleteShift = (shift) => {
    setShiftToDelete(shift);
    setIsBulkDelete(false);
    setShowDeleteShiftModal(true);
  };

  const handleBulkDeleteShifts = () => {
    if (selectedShiftRows.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteShiftModal(true);
  };

  const confirmDeleteShift = async () => {
    if (!shiftToDelete && !isBulkDelete) return;

    try {
      if (isBulkDelete) {
        const ids = Array.from(selectedShiftRows);
        let successCount = 0;
        let failCount = 0;

        for (const id of ids) {
          try {
            await deleteShiftMutation.mutateAsync(id);
            successCount++;
          } catch (error) {
            failCount++;
          }
        }

        if (successCount > 0) {
          showSuccess(`${successCount} shift(s) deleted successfully.`);
          setSelectedShiftRows(new Set());
        }
        if (failCount > 0) {
          showError(`${failCount} shift(s) could not be deleted.`);
        }
      } else {
        await deleteShiftMutation.mutateAsync(shiftToDelete.id);
        showSuccess(`Shift "${shiftToDelete.name}" deleted successfully!`);
      }
      setShowDeleteShiftModal(false);
      setShiftToDelete(null);
      setIsBulkDelete(false);
      await loadShifts();
    } catch (error) {
      if (!isBulkDelete) {
        showError("Failed to delete shift. Please try again.");
      }
    }
  };

  const cancelDeleteShift = () => {
    setShowDeleteShiftModal(false);
    setShiftToDelete(null);
    setIsBulkDelete(false);
  };

  const handleDeleteRoster = (rosterEntry) => {
    setRosterToDelete(rosterEntry);
    setIsBulkDelete(false);
    setShowDeleteRosterModal(true);
  };

  const handleBulkDeleteRoster = () => {
    if (selectedRosterRows.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteRosterModal(true);
  };

  const confirmDeleteRoster = async () => {
    if (!rosterToDelete && !isBulkDelete) return;

    try {
      if (isBulkDelete) {
        const ids = Array.from(selectedRosterRows);
        let successCount = 0;
        let failCount = 0;

        for (const id of ids) {
          try {
            await deleteRosterMutation.mutateAsync(id);
            successCount++;
          } catch (error) {
            failCount++;
          }
        }

        if (successCount > 0) {
          showSuccess(`${successCount} roster assignment(s) deleted successfully.`);
          setSelectedRosterRows(new Set());
        }
        if (failCount > 0) {
          showError(`${failCount} roster assignment(s) could not be deleted.`);
        }
      } else {
        await deleteRosterMutation.mutateAsync(rosterToDelete.id);
        showSuccess("✅ Roster assignment deleted successfully!");
      }
      setShowDeleteRosterModal(false);
      setRosterToDelete(null);
      setIsBulkDelete(false);
      await loadRoster();
    } catch (error) {
      if (!isBulkDelete) {
        showError("Failed to delete roster. Please try again.");
      }
    }
  };

  const cancelDeleteRoster = () => {
    setShowDeleteRosterModal(false);
    setRosterToDelete(null);
    setIsBulkDelete(false);
  };

  const handleViewDetails = async (rosterEntry) => {
    try {
      // Fetch full roster details from API to get all information
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/roster-test/${rosterEntry.id}`,
        {
          method: "GET",
          headers: buildJsonHeaders(),
        }
      );

      if (response.ok) {
        const result = await response.json();
        const fullRosterData = result.data;

        // Transform the full roster data to include all fields
        const detailedRoster = {
          id: fullRosterData.id || fullRosterData._id,
          employeeId: fullRosterData.employeeId,
          employeeName: fullRosterData.employeeId?.personalInfo?.firstName && fullRosterData.employeeId?.personalInfo?.lastName
            ? `${fullRosterData.employeeId.personalInfo.firstName} ${fullRosterData.employeeId.personalInfo.lastName}`
            : fullRosterData.employeeId?.fullName || rosterEntry.employeeName || "Unknown Employee",
          employeeEmail: fullRosterData.employeeId?.contactInfo?.personalEmail || rosterEntry.employeeEmail || "N/A",
          employeeEmployeeId: fullRosterData.employeeId?.employeeId || "N/A",
          shiftId: fullRosterData.shiftId,
          shiftName: fullRosterData.shiftId?.name || rosterEntry.shiftName || "Unknown Shift",
          shiftStartTime: fullRosterData.shiftId?.startTime || "N/A",
          shiftEndTime: fullRosterData.shiftId?.endTime || "N/A",
          shiftBreakDuration: fullRosterData.shiftId?.breakDuration || 0,
          shiftTotalWorkingHours: fullRosterData.shiftId?.totalWorkingHours || "N/A",
          shiftGraceCheckInTime: fullRosterData.shiftId?.graceCheckInTime ?? rosterEntry.shiftGraceCheckInTime ?? 0,
          shiftGraceDays: fullRosterData.shiftId?.graceDays ?? rosterEntry.shiftGraceDays ?? 0,
          shiftGraceDaysTime: fullRosterData.shiftId?.graceDaysTime ?? rosterEntry.shiftGraceDaysTime ?? 0,
          startDate: fullRosterData.startDate,
          endDate: fullRosterData.endDate,
          isRecurring: fullRosterData.isRecurring,
          recurringDays: fullRosterData.recurringDays || [],
          recurringPattern: fullRosterData.recurringPattern,
          notes: fullRosterData.notes || "",
          status: fullRosterData.status,
          assignmentType: fullRosterData.assignmentType,
          priority: fullRosterData.priority,
          requiresApproval: fullRosterData.requiresApproval,
          approvalStatus: fullRosterData.approvalStatus,
          attendanceRate: fullRosterData.attendanceRate,
          emergencyContact: fullRosterData.emergencyContact || {},
          specialRequirements: fullRosterData.specialRequirements || [],
          workLocation: fullRosterData.workLocation || "",
          isRemote: fullRosterData.isRemote,
          duration: fullRosterData.duration,
          isCurrentlyActive: fullRosterData.isCurrentlyActive,
          isUpcoming: fullRosterData.isUpcoming,
          isOverdue: fullRosterData.isOverdue,
          createdBy: fullRosterData.createdBy,
          lastModifiedBy: fullRosterData.lastModifiedBy,
          version: fullRosterData.version,
          createdAt: fullRosterData.createdAt,
          updatedAt: fullRosterData.updatedAt,
        };

        setSelectedRoster(detailedRoster);
        setShowDetails(true);
      } else {
        // If API call fails, use the roster entry data we have
        setSelectedRoster(rosterEntry);
        setShowDetails(true);
      }
    } catch (error) {
      // If error, use the roster entry data we have
      setSelectedRoster(rosterEntry);
      setShowDetails(true);
    }
  };

  const toggleShiftStatus = async (shiftId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/shifts/${shiftId}/toggle`,
        {
          method: "PATCH",
          headers: buildJsonHeaders(),
        }
      );

      if (response.ok) {
        const result = await response.json();
        showSuccess(
          `Shift ${result.data.isActive ? "activated" : "deactivated"
          } successfully!`
        );
        await loadShifts();
        await loadRoster();
      } else {
        const errorResult = await response.json();
        showError(
          "Failed to toggle shift status: " +
          (errorResult.message || "Unknown error")
        );
      }
    } catch (error) {
      showError("Failed to toggle shift status. Please try again.");
    }
  };

  // Use filteredRoster for display (includes client-side search filtering)
  // Server-side pagination and status filtering are already applied
  const paginatedRoster = filteredRoster.length > 0 ? filteredRoster : roster;

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleExportRoster = async () => {
    try {
      // Fetch all roster data matching current filters (not just current page)
      const params = new URLSearchParams({
        page: '1',
        limit: '10000',
      });

      // Add status filter
      if (filterStatus && filterStatus !== "all") {
        params.append("status", filterStatus);
      }

      // Add search term to backend
      if (searchTerm && searchTerm.trim()) {
        params.append("search", searchTerm.trim());
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/roster-test?${params.toString()}`,
        {
          method: "GET",
          headers: buildJsonHeaders(),
        }
      );

      let dataToExport = [];
      if (response.ok) {
        const result = await response.json();
        const rosters = result.data?.rosters || [];

        // Transform rosters for export
        dataToExport = rosters.map((roster) => {
          let employeeName = "N/A";
          let employeeEmail = "N/A";

          if (roster.employeeId) {
            if (roster.employeeId.personalInfo?.firstName && roster.employeeId.personalInfo?.lastName) {
              employeeName = `${roster.employeeId.personalInfo.firstName} ${roster.employeeId.personalInfo.lastName}`;
            } else if (roster.employeeId.fullName) {
              employeeName = roster.employeeId.fullName;
            }

            employeeEmail = roster.employeeId.contactInfo?.personalEmail || "N/A";
          }

          return {
            "Employee Name": employeeName,
            "Email": employeeEmail,
            "Shift Name": roster.shiftId?.name || "N/A",
            "Start Date": roster.startDate ? formatDate(roster.startDate, { includeTime: false }) : "N/A",
            "End Date": roster.endDate ? formatDate(roster.endDate, { includeTime: false }) : "N/A",
            "Status": roster.status || "N/A",
          };
        });
      } else {
        // Fallback to current page data
        const currentRoster = filteredRoster.length > 0 ? filteredRoster : roster;
        dataToExport = currentRoster.map((entry) => ({
          "Employee Name": entry.employeeName || "N/A",
          "Email": entry.employeeEmail || "N/A",
          "Shift Name": entry.shiftName || "N/A",
          "Start Date": entry.startDate ? formatDate(entry.startDate, { includeTime: false }) : "N/A",
          "End Date": entry.endDate ? formatDate(entry.endDate, { includeTime: false }) : "N/A",
          "Status": entry.status || "N/A",
        }));
      }

      if (!dataToExport || dataToExport.length === 0) {
        return;
      }

      // Convert to CSV format
      const csvContent = [
        // Header row
        Object.keys(dataToExport[0] || {}).join(","),
        // Data rows
        ...dataToExport.map((row) =>
          Object.values(row)
            .map((value) =>
              typeof value === "string" && value.includes(",")
                ? `"${value.replace(/"/g, '""')}"`
                : value
            )
            .join(",")
        ),
      ].join("\n");

      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `roster_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
    }
  };

  const handleExportShifts = async () => {
    try {
      // Fetch all shifts data
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/shifts`,
        {
          method: "GET",
          headers: buildJsonHeaders(),
        }
      );

      let dataToExport = [];
      if (response.ok) {
        const result = await response.json();
        const shiftsData = result.data?.shifts || [];

        // Transform shifts for export
        dataToExport = shiftsData.map((shift) => {
          // Calculate working hours if not present
          let workingHours = shift.workingHours || shift.totalWorkingHours || "";
          if (!workingHours && shift.startTime && shift.endTime) {
            const start = new Date(`2024-01-01T${shift.startTime}:00`);
            const end = new Date(`2024-01-01T${shift.endTime}:00`);
            if (end < start) {
              end.setDate(end.getDate() + 1);
            }
            const diffMs = end - start;
            const diffHours = diffMs / (1000 * 60 * 60);
            workingHours = Math.max(0, diffHours).toFixed(1);
          }

          return {
            "Shift Name": shift.name || "N/A",
            "Type": shift.shiftType || "N/A",
            "Start Time": shift.startTime || "N/A",
            "End Time": shift.endTime || "N/A",
            "Total Working Hours": workingHours ? `${workingHours} hours` : "N/A",
            "Break Duration": shift.breakDuration ? `${shift.breakDuration} minutes` : "N/A",
            "Status": shift.isActive ? "Active" : "Inactive",
          };
        });
      } else {
        // Fallback to current shifts data
        dataToExport = shifts.map((shift) => {
          let workingHours = shift.workingHours || shift.totalWorkingHours || "";
          if (!workingHours && shift.startTime && shift.endTime) {
            const start = new Date(`2024-01-01T${shift.startTime}:00`);
            const end = new Date(`2024-01-01T${shift.endTime}:00`);
            if (end < start) {
              end.setDate(end.getDate() + 1);
            }
            const diffMs = end - start;
            const diffHours = diffMs / (1000 * 60 * 60);
            workingHours = Math.max(0, diffHours).toFixed(1);
          }

          return {
            "Shift Name": shift.name || "N/A",
            "Type": shift.shiftType || "N/A",
            "Start Time": shift.startTime || "N/A",
            "End Time": shift.endTime || "N/A",
            "Total Working Hours": workingHours ? `${workingHours} hours` : "N/A",
            "Break Duration": shift.breakDuration ? `${shift.breakDuration} minutes` : "N/A",
            "Status": shift.isActive ? "Active" : "Inactive",
          };
        });
      }

      if (!dataToExport || dataToExport.length === 0) {
        return;
      }

      // Convert to CSV format
      const csvContent = [
        // Header row
        Object.keys(dataToExport[0] || {}).join(","),
        // Data rows
        ...dataToExport.map((row) =>
          Object.values(row)
            .map((value) =>
              typeof value === "string" && value.includes(",")
                ? `"${value.replace(/"/g, '""')}"`
                : value
            )
            .join(",")
        ),
      ].join("\n");

      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `shifts_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
    }
  };

  const handleExport = () => {
    if (viewMode === "roster") {
      handleExportRoster();
    } else if (viewMode === "shifts") {
      handleExportShifts();
    }
  };

  // Row selection handlers for Roster tab
  const handleSelectAllRoster = (e) => {
    const currentPageIds = filteredRoster.map(entry => entry.id);
    const selectedOnPage = currentPageIds.filter(id => selectedRosterRows.has(id));

    if (selectedOnPage.length > 0) {
      // If any selected on current page, clear EVERYTHING
      setSelectedRosterRows(new Set());
    } else {
      // Otherwise select all on current page
      const newSelected = new Set(selectedRosterRows);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedRosterRows(newSelected);
    }
  };

  const handleSelectRosterRow = (id) => {
    const newSelected = new Set(selectedRosterRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRosterRows(newSelected);
  };

  // Row selection handlers for Shifts tab
  const handleSelectAllShifts = (e) => {
    const currentPageIds = shifts.map(shift => shift.id);
    const selectedOnPage = currentPageIds.filter(id => selectedShiftRows.has(id));

    if (selectedOnPage.length > 0) {
      // If any selected on current page, clear EVERYTHING
      setSelectedShiftRows(new Set());
    } else {
      // Otherwise select all on current page
      const newSelected = new Set(selectedShiftRows);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedShiftRows(newSelected);
    }
  };

  const handleSelectShiftRow = (id) => {
    const newSelected = new Set(selectedShiftRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedShiftRows(newSelected);
  };

  // Map roster data for CSV export
  const mapRosterForExport = (rosterData) => {
    return rosterData.map(r => ({
      'Employee Name': r.employeeName || 'N/A',
      'Employee ID': r.businessEmployeeId || r.employeeId || 'N/A',
      'Employee Email': r.employeeEmail || 'N/A',
      'Shift Name': r.shiftName || 'N/A',
      'Start Date': r.startDate ? formatDate(r.startDate, { includeTime: false }) : 'N/A',
      'End Date': r.endDate ? formatDate(r.endDate, { includeTime: false }) : 'N/A',
      'Status': r.status || 'N/A',
      'Assignment Type': r.assignmentType || 'N/A',
      'Notes': r.notes || '',
      'Created At': r.createdAt ? formatDate(r.createdAt) : 'N/A'
    }));
  };

  // Map shifts data for CSV export
  const mapShiftsForExport = (shiftsData) => {
    return shiftsData.map(s => ({
      'Shift Name': s.name || 'N/A',
      'Shift Type': s.shiftType || 'Regular',
      'Start Time': formatTimeAMPM(s.startTime) || 'N/A',
      'End Time': formatTimeAMPM(s.endTime) || 'N/A',
      'Break Duration (min)': s.breakDuration || 0,
      'Total Working Hours': (() => {
        const hours = s.workingHours || s.totalWorkingHours;
        if (hours) return typeof hours === 'number' ? hours.toFixed(1) : hours;
        if (s.startTime && s.endTime) {
          const start = new Date(`2024-01-01T${s.startTime}:00`);
          const end = new Date(`2024-01-01T${s.endTime}:00`);
          if (end < start) end.setDate(end.getDate() + 1);
          const diffMs = end - start;
          const diffHours = diffMs / (1000 * 60 * 60);
          return Math.max(0, diffHours).toFixed(1);
        }
        return 'N/A';
      })(),
      'Department': s.department || 'All',
      'Max Employees': s.maxEmployees || 50,
      'Status': s.isActive ? 'Active' : 'Inactive',
      'Description': s.description || '',
      'Created At': s.createdAt ? formatDate(s.createdAt) : 'N/A'
    }));
  };

  // Click-outside handler for inline export format bar
  useEffect(() => {
    const onMouseDown = (event) => {
      if (!showInlineSelectedFormats) return;
      const target = event.target;
      const wrap = selectedExportWrapRef.current;
      const bar = target.closest('[data-export-format-bar]');
      if (wrap && !wrap.contains(target) && !bar) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  // Export handlers for Roster tab
  const handleExportRosterClick = () => {
    if (selectedRosterRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  // Build base export filters for Roster tab
  const baseRosterExportFilters = useMemo(() => {
    const apiFilters = {
      search: searchTerm || undefined,
      status: filterStatus !== 'all' ? filterStatus : undefined,
      startDate: filterStartDate || undefined,
      endDate: filterEndDate || undefined,
    };

    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) delete apiFilters[key];
    });

    return apiFilters;
  }, [searchTerm, filterStatus, filterStartDate, filterEndDate]);

  // Get current page roster IDs
  const currentPageRosterIds = useMemo(
    () =>
      filteredRoster
        .map((r) => {
          const raw = r?._id ?? r?.id;
          return raw != null && raw !== '' ? String(raw) : '';
        })
        .filter(Boolean),
    [filteredRoster]
  );

  // Unified backend export function for Roster tab using /exports/generate endpoint
  const requestRosterBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const token = localStorage.getItem('auth_token');
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageRosterIds)
          : [];
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            module: 'shift-roster',
            format,
            scope,
            filters: overrideFilters ?? baseRosterExportFilters,
            page: overridePage ?? currentPage,
            limit: overrideLimit ?? itemsPerPage,
            sortBy: sortField,
            sortOrder,
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `shift-roster-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseRosterExportFilters,
      currentPage,
      itemsPerPage,
      sortField,
      sortOrder,
      currentPageRosterIds,
    ]
  );

  // Handle export for selected roster rows with format selection
  const handleRosterSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRosterRows).map(String).filter(Boolean);
      if (!ids.length) {
        showWarning('No selected roster rows to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestRosterBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        showSuccess('Export started.');
        setShowInlineSelectedFormats(false);
      } catch (error) {
        console.error(error);
        showError(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRosterRows, requestRosterBackendExport, showSuccess, showWarning, showError]
  );

  // Handle scoped export from modal for Roster tab
  const handleRosterScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }
      setExportLoading(true);
      try {
        if (scope === 'dateRange') {
          const { startDate, endDate } = meta;
          if (!startDate || !endDate) {
            showWarning('Please select both start and end dates.');
            return;
          }
          await requestRosterBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseRosterExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestRosterBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: itemsPerPage,
          });
        } else if (scope === 'filtered') {
          await requestRosterBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestRosterBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPageRosterIds,
          });
        } else {
          return;
        }

        showSuccess('Export started.');
        setShowExportModal(false);
      } catch (error) {
        console.error(error);
        showError(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      filteredRoster.length,
      baseRosterExportFilters,
      requestRosterBackendExport,
      itemsPerPage,
      currentPageRosterIds,
      showSuccess,
      showWarning,
      showError,
    ]
  );

  // Legacy handlers for backward compatibility (deprecated)
  const handleExportRosterCurrentPage = () => {
    const exportData = mapRosterForExport(filteredRoster);
    exportToCSV(exportData, 'roster_current_page', {
      title: 'Roster Assignments',
      type: 'Current Page'
    });
    setShowExportModal(false);
    showSuccess('Current page exported!');
  };

  const handleExportRosterRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("type", `Date Range (${startDate} to ${endDate})`);
      if (startDate) queryParams.append("startDate", startDate);
      if (endDate) queryParams.append("endDate", endDate);
      if (searchTerm) queryParams.append("search", searchTerm);
      if (filterStatus !== "all") queryParams.append("status", filterStatus);

      const url = `${process.env.REACT_APP_API_BASE_URL}/roster-test/export?${queryParams.toString()}`;
      const filename = generateExportFilename('roster_range');
      await downloadFromBackend(url, filename);

      setShowExportModal(false);
      showSuccess('Roster export started successfully');
    } catch (error) {
      showError("An error occurred during roster export");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportRosterAll = async () => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("type", "All Data");
      if (searchTerm) queryParams.append("search", searchTerm);
      if (filterStatus !== "all") queryParams.append("status", filterStatus);

      const url = `${process.env.REACT_APP_API_BASE_URL}/roster-test/export?${queryParams.toString()}`;
      const filename = generateExportFilename('all_roster');
      await downloadFromBackend(url, filename);

      setShowExportModal(false);
      showSuccess('All roster data export started');
    } catch (error) {
      showError("An error occurred during roster export");
    } finally {
      setExportLoading(false);
    }
  };

  // Export handlers for Shifts tab
  const handleExportShiftsClick = () => {
    if (selectedShiftRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  // Build base export filters for Shifts tab (shifts don't have status filter)
  const baseShiftExportFilters = useMemo(() => {
    const apiFilters = {
      search: searchTerm || undefined,
    };

    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) delete apiFilters[key];
    });

    return apiFilters;
  }, [searchTerm]);

  // Get current page shift IDs
  const currentPageShiftIds = useMemo(
    () =>
      shifts
        .map((s) => {
          const raw = s?._id ?? s?.id;
          return raw != null && raw !== '' ? String(raw) : '';
        })
        .filter(Boolean),
    [shifts]
  );

  // Unified backend export function for Shifts tab using /exports/generate endpoint
  const requestShiftBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const token = localStorage.getItem('auth_token');
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageShiftIds)
          : [];
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            module: 'shift-master',
            format,
            scope,
            filters: overrideFilters ?? baseShiftExportFilters,
            page: overridePage ?? currentPage,
            limit: overrideLimit ?? itemsPerPage,
            sortBy: sortField,
            sortOrder,
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `shift-master-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseShiftExportFilters,
      currentPage,
      itemsPerPage,
      sortField,
      sortOrder,
      currentPageShiftIds,
    ]
  );

  // Handle export for selected shift rows with format selection
  const handleShiftSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedShiftRows).map(String).filter(Boolean);
      if (!ids.length) {
        showWarning('No selected shift rows to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestShiftBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        showSuccess('Export started.');
        setShowInlineSelectedFormats(false);
      } catch (error) {
        console.error(error);
        showError(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedShiftRows, requestShiftBackendExport, showSuccess, showWarning, showError]
  );

  // Handle scoped export from modal for Shifts tab
  const handleShiftScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }
      setExportLoading(true);
      try {
        if (scope === 'dateRange') {
          const { startDate, endDate } = meta;
          if (!startDate || !endDate) {
            showWarning('Please select both start and end dates.');
            return;
          }
          await requestShiftBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseShiftExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestShiftBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: itemsPerPage,
          });
        } else if (scope === 'filtered') {
          await requestShiftBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestShiftBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPageShiftIds,
          });
        } else {
          return;
        }

        showSuccess('Export started.');
        setShowExportModal(false);
      } catch (error) {
        console.error(error);
        showError(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      shifts.length,
      baseShiftExportFilters,
      requestShiftBackendExport,
      itemsPerPage,
      currentPageShiftIds,
      showSuccess,
      showWarning,
      showError,
    ]
  );

  // Legacy handlers for backward compatibility (deprecated)
  const handleExportShiftsCurrentPage = () => {
    const exportData = mapShiftsForExport(shifts);
    exportToCSV(exportData, 'shifts_current_page', {
      title: 'Shifts',
      type: 'Current Page'
    });
    setShowExportModal(false);
    showSuccess('Current page exported!');
  };

  const handleExportShiftsRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("type", `Date Range (${startDate} to ${endDate})`);
      if (startDate) queryParams.append("startDate", startDate);
      if (endDate) queryParams.append("endDate", endDate);

      const url = `${process.env.REACT_APP_API_BASE_URL}/shifts/export?${queryParams.toString()}`;
      const filename = generateExportFilename('shifts_range');
      await downloadFromBackend(url, filename);

      setShowExportModal(false);
      showSuccess('Shifts export started successfully');
    } catch (error) {
      showError("An error occurred during shifts export");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportShiftsAll = async () => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("type", "All Data");

      const url = `${process.env.REACT_APP_API_BASE_URL}/shifts/export?${queryParams.toString()}`;
      const filename = generateExportFilename('all_shifts');
      await downloadFromBackend(url, filename);

      setShowExportModal(false);
      showSuccess('All shifts data export started');
    } catch (error) {
      showError("An error occurred during shifts export");
    } finally {
      setExportLoading(false);
    }
  };

  if (showDetails && selectedRoster) {
    return (
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDetails(false)}
              className="p-2"
              aria-label="Back to shift roster list"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-xl font-semibold text-gray-900 break-words whitespace-normal">
              Roster Assignment Details
            </h2>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Employee Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <User className="h-5 w-5 mr-2 text-gray-600 flex-shrink-0" />
              <span className="break-words whitespace-normal">Employee Information</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-gray-600">Employee Name:</span>
                <div className="text-sm font-medium text-gray-900 mt-1 break-words whitespace-normal">
                  {selectedRoster.employeeName || "N/A"}
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-600">Email:</span>
                <div className="text-sm font-medium text-gray-900 mt-1 break-words whitespace-normal">
                  {selectedRoster.employeeEmail || "N/A"}
                </div>
              </div>
            </div>
          </div>

          {/* Shift Information */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Clock className="h-5 w-5 mr-2 text-brand flex-shrink-0" />
              <span className="break-words whitespace-normal">Shift Information</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <span className="text-sm text-gray-600">Shift Name:</span>
                <div className="text-sm font-medium text-gray-900 mt-1 break-words whitespace-normal capitalize">
                  {selectedRoster.shiftName || "N/A"}
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-600">Start Time:</span>
                <div className="text-sm font-medium text-gray-900 mt-1 break-words whitespace-normal">
                  {formatTimeAMPM(selectedRoster.shiftStartTime) || "N/A"}
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-600">End Time:</span>
                <div className="text-sm font-medium text-gray-900 mt-1 break-words whitespace-normal">
                  {formatTimeAMPM(selectedRoster.shiftEndTime) || "N/A"}
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-600">Break Duration:</span>
                <div className="text-sm font-medium text-gray-900 mt-1 break-words whitespace-normal">
                  {selectedRoster.shiftBreakDuration || "0"} minutes
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-600">Grace Check-In:</span>
                <div className="text-sm font-medium text-gray-900 mt-1 break-words whitespace-normal">
                  {selectedRoster.shiftGraceCheckInTime || 0} minutes
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-600">Monthly Grace Days:</span>
                <div className="text-sm font-medium text-gray-900 mt-1 break-words whitespace-normal">
                  {selectedRoster.shiftGraceDays || 0} days/month
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-600">Grace Days Timing:</span>
                <div className="text-sm font-medium text-gray-900 mt-1 break-words whitespace-normal">
                  {selectedRoster.shiftGraceDaysTime || 0} minutes
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-600">Total Working Hours:</span>
                <div className="text-sm font-medium text-gray-900 mt-1 break-words whitespace-normal">
                  {(() => {
                    const calculatedHours = calculateWorkingHours(
                      selectedRoster.shiftStartTime,
                      selectedRoster.shiftEndTime,
                      selectedRoster.shiftBreakDuration
                    );
                    return calculatedHours !== null ? `${calculatedHours} hours` : "N/A";
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* Schedule Information */}
          <div className="bg-green-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-green-600 flex-shrink-0" />
              <span className="break-words whitespace-normal">Schedule Information</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <span className="text-sm text-gray-600">Start Date:</span>
                <div className="text-sm font-medium text-gray-900 mt-1 break-words whitespace-normal">
                  {selectedRoster.startDate
                    ? formatDate(selectedRoster.startDate, { includeTime: false })
                    : "N/A"}
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-600">End Date:</span>
                <div className="text-sm font-medium text-gray-900 mt-1 break-words whitespace-normal">
                  {selectedRoster.endDate
                    ? formatDate(selectedRoster.endDate, { includeTime: false })
                    : "N/A"}
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-600">Duration:</span>
                <div className="text-sm font-medium text-gray-900 mt-1 break-words whitespace-normal">
                  {selectedRoster.duration !== undefined && selectedRoster.duration !== null
                    ? `${selectedRoster.duration} day(s)`
                    : selectedRoster.startDate && selectedRoster.endDate
                      ? `${Math.ceil((new Date(selectedRoster.endDate) - new Date(selectedRoster.startDate)) / (1000 * 60 * 60 * 24))} day(s)`
                      : "N/A"}
                </div>
              </div>
            </div>
          </div>

          {/* Special Requirements */}
          {selectedRoster.specialRequirements && selectedRoster.specialRequirements.length > 0 && (
            <div className="bg-yellow-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Timer className="h-5 w-5 mr-2 text-yellow-600 flex-shrink-0" />
                <span className="break-words whitespace-normal">Special Requirements</span>
              </h3>
              <div className="space-y-2">
                {selectedRoster.specialRequirements.map((req, index) => (
                  <div key={index} className="text-sm text-gray-700 bg-white rounded p-2 break-words whitespace-normal">
                    {typeof req === "string" ? req : JSON.stringify(req)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Emergency Contact */}
          {selectedRoster.emergencyContact && Object.keys(selectedRoster.emergencyContact).length > 0 && (
            <div className="bg-red-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <User className="h-5 w-5 mr-2 text-red-600 flex-shrink-0" />
                <span className="break-words whitespace-normal">Emergency Contact</span>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {selectedRoster.emergencyContact.name && (
                  <div>
                    <span className="text-sm text-gray-600">Name:</span>
                    <div className="text-sm font-medium text-gray-900 mt-1 break-words whitespace-normal">
                      {selectedRoster.emergencyContact.name}
                    </div>
                  </div>
                )}
                {selectedRoster.emergencyContact.phone && (
                  <div>
                    <span className="text-sm text-gray-600">Phone:</span>
                    <div className="text-sm font-medium text-gray-900 mt-1 break-words whitespace-normal">
                      {selectedRoster.emergencyContact.phone}
                    </div>
                  </div>
                )}
                {selectedRoster.emergencyContact.relationship && (
                  <div>
                    <span className="text-sm text-gray-600">Relationship:</span>
                    <div className="text-sm font-medium text-gray-900 mt-1 break-words whitespace-normal">
                      {selectedRoster.emergencyContact.relationship}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-gray-600 flex-shrink-0" />
              <span className="break-words whitespace-normal">Notes</span>
            </h3>
            <div className="text-sm text-gray-700 bg-white rounded-lg p-3 break-words whitespace-normal first-letter:capitalize">
              {selectedRoster.notes || "No notes provided"}
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-slate-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Settings className="h-5 w-5 mr-2 text-slate-600 flex-shrink-0" />
              <span className="break-words whitespace-normal">Metadata</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-gray-600">Created At:</span>
                <div className="text-sm font-medium text-gray-900 mt-1 break-words whitespace-normal">
                  {selectedRoster.createdAt
                    ? formatDate(selectedRoster.createdAt)
                    : "N/A"}
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-600">Last Updated:</span>
                <div className="text-sm font-medium text-gray-900 mt-1 break-words whitespace-normal">
                  {selectedRoster.updatedAt
                    ? formatDate(selectedRoster.updatedAt)
                    : "N/A"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showShiftForm) {
    return (
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowShiftForm(false);
                setEditingShift(null);
                if (onClose) onClose();
              }}
              className="p-2"
              aria-label="Back to shift roster"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-xl font-semibold text-gray-900">
              {editingShift ? "Edit Shift" : "New Shift"}
            </h2>
          </div>
        </div>

        <form onSubmit={handleSubmitShift} className="p-6">
          <div className="mx-auto space-y-6">
            {/* Basic Information */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Shift Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={shiftFormData.name}
                      onChange={handleShiftInputChange}
                      onBlur={handleShiftBlur}
                      required
                      placeholder="e.g., Morning Shift, Night Shift"
                      className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${(shiftFormSubmitted || shiftTouched.name) && shiftFormErrors.name ? "border-red-300 bg-red-50" : "border-gray-300"}`}
                    />
                    <div className="flex justify-end text-xs text-gray-400 mt-1">
                      <span>{shiftFormData.name.length}/{TEXT_LIMITS.shiftName}</span>
                    </div>
                    {(shiftFormSubmitted || shiftTouched.name) && shiftFormErrors.name && (
                      <p className="text-xs text-red-500 mt-1">{shiftFormErrors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Shift Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="shiftType"
                      value={shiftFormData.shiftType}
                      onChange={handleShiftInputChange}
                      onBlur={handleShiftBlur}
                      required
                      className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${(shiftFormSubmitted || shiftTouched.shiftType) && shiftFormErrors.shiftType ? "border-red-300 bg-red-50" : "border-gray-300"}`}
                    >
                      <option value="">Select Shift Type</option>
                      <option value="Regular">Regular</option>
                      <option value="Flexi">Flexi</option>
                      <option value="Part-Time">Part-Time</option>
                      <option value="Weekend">Weekend</option>
                    </select>
                    {(shiftFormSubmitted || shiftTouched.shiftType) && shiftFormErrors.shiftType && (
                      <p className="text-xs text-red-500 mt-1">{shiftFormErrors.shiftType}</p>
                    )}
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-full ${shiftFormData.isFlexible ? 'bg-blue-100 text-[#0F2942]' : 'bg-gray-100 text-gray-400'}`}>
                      <Timer className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Flexible Shift</p>
                      <p className="text-xs text-gray-500">Enable this to allow flexible check-in and check-out times</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleShiftInputChange({ target: { name: 'isFlexible', value: !shiftFormData.isFlexible, type: 'checkbox', checked: !shiftFormData.isFlexible } })}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${shiftFormData.isFlexible ? 'bg-[#0F2942]' : 'bg-gray-200'}`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${shiftFormData.isFlexible ? 'translate-x-5' : 'translate-x-0'}`}
                    />
                  </button>
                </div>
              </div>

              {/* Time Configuration */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Time Configuration</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      name="startTime"
                      value={shiftFormData.startTime}
                      onChange={handleShiftInputChange}
                      onBlur={handleShiftBlur}
                      onClick={(e) => e.target.showPicker?.()}
                      required
                      className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${(shiftFormSubmitted || shiftTouched.startTime) && shiftFormErrors.startTime ? "border-red-300 bg-red-50" : "border-gray-300"}`}
                    />
                    {(shiftFormSubmitted || shiftTouched.startTime) && shiftFormErrors.startTime && (
                      <p className="text-xs text-red-500 mt-1">{shiftFormErrors.startTime}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      name="endTime"
                      value={shiftFormData.endTime}
                      onChange={handleShiftInputChange}
                      onBlur={handleShiftBlur}
                      onClick={(e) => e.target.showPicker?.()}
                      required
                      className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${(shiftFormSubmitted || shiftTouched.endTime) && shiftFormErrors.endTime ? "border-red-300 bg-red-50" : "border-gray-300"}`}
                    />
                    {(shiftFormSubmitted || shiftTouched.endTime) && shiftFormErrors.endTime && (
                      <p className="text-xs text-red-500 mt-1">{shiftFormErrors.endTime}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Break Duration (minutes)
                    </label>
                    <input
                      type="number"
                      name="breakDuration"
                      value={shiftFormData.breakDuration}
                      onChange={handleShiftInputChange}
                      min="0"
                      max="480"
                      placeholder="60"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                    />
                    <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
                      <span>Positive numbers only</span>
                      <span>{shiftFormData.breakDuration || "0"}/{NUMBER_LIMITS.breakDuration}</span>
                    </div>
                  </div>
                </div>
                {shiftFormData.startTime && shiftFormData.endTime && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg space-y-2">
                    <div className="pt-2 border-t border-gray-200 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-brand" />
                        <span className="text-sm font-bold text-gray-800">Total Working Hours:</span>
                      </div>
                      <span className="text-lg font-bold text-brand">
                        {shiftFormData.workingHours} hours
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Attendance Rules */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Attendance Rules</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Grace Check-in (min)
                    </label>
                    <input
                      type="number"
                      name="graceCheckInTime"
                      value={shiftFormData.graceCheckInTime}
                      onChange={handleShiftInputChange}
                      min="0"
                      placeholder="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                    />
                    <p className="text-xs text-gray-500 mt-1">Check-in grace allowance in minutes</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Grace Days (per month)
                    </label>
                    <input
                      type="number"
                      name="graceDays"
                      value={shiftFormData.graceDays}
                      onChange={handleShiftInputChange}
                      min="0"
                      placeholder="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                    />
                    <p className="text-xs text-gray-500 mt-1">Allowed late arrivals within grace time per month</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Grace Days Timing (min)
                    </label>
                    <input
                      type="number"
                      name="graceDaysTime"
                      value={shiftFormData.graceDaysTime}
                      onChange={handleShiftInputChange}
                      min="0"
                      placeholder="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                    />
                    <p className="text-xs text-gray-500 mt-1">Grace time allowance for grace days in minutes</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Grace Check-out (min)
                    </label>
                    <input
                      type="number"
                      name="graceCheckOutTime"
                      value={shiftFormData.graceCheckOutTime}
                      onChange={handleShiftInputChange}
                      min="0"
                      placeholder="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Half Day Rule (hr)
                    </label>
                    <input
                      type="number"
                      name="halfDayRule"
                      value={shiftFormData.halfDayRule}
                      onChange={handleShiftInputChange}
                      min="0"
                      step="0.5"
                      placeholder="4"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                    />
                    <p className="text-xs text-gray-500 mt-1">If worked less than [hr]</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Absent Rule (hr)
                    </label>
                    <input
                      type="number"
                      name="absentRule"
                      value={shiftFormData.absentRule}
                      onChange={handleShiftInputChange}
                      min="0"
                      step="0.5"
                      placeholder="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                    />
                    <p className="text-xs text-gray-500 mt-1">Consider absent if worked less than [hr]</p>
                  </div>
                </div>
              </div>

              {/* Working Days & Weekly Holidays */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Working Days <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => handleShiftDayToggle(day, 'workingDays')}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${shiftFormData.workingDays.includes(day)
                          ? "bg-green-100 border-green-500 text-green-700"
                          : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"
                          }`}
                      >
                        {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                      </button>
                    ))}
                  </div>
                  {shiftFormErrors.workingDays && (
                    <p className="text-xs text-red-500 mt-2">{shiftFormErrors.workingDays}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Weekly Holidays
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => handleShiftDayToggle(day, 'weeklyHolidays')}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${shiftFormData.weeklyHolidays.includes(day)
                          ? "bg-red-100 border-red-500 text-red-700"
                          : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"
                          }`}
                      >
                        {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Saturday Working */}
              <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                <label className="block text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <div className="w-1 h-4 bg-brand rounded-full"></div>
                  Saturday Working
                </label>
                <div className="flex flex-wrap gap-3">
                  {['1st', '2nd', '3rd', '4th', '5th'].map((num, index) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => handleShiftSaturdayToggle(index + 1)}
                      className={`px-4 py-2 text-xs font-semibold rounded-xl border-2 transition-all duration-200 flex items-center gap-2 ${shiftFormData.saturdayWorking?.includes(index + 1)
                        ? "bg-brand/5 border-brand text-brand shadow-sm shadow-brand/10"
                        : "bg-gray-50 border-gray-100/50 text-gray-500 hover:border-gray-300 hover:bg-white"
                        }`}
                    >
                      <div className={`w-1.5 h-1.5 rounded-full transition-all duration-200 ${shiftFormData.saturdayWorking?.includes(index + 1) 
                        ? 'bg-brand animate-pulse scale-100 opacity-100' 
                        : 'bg-transparent scale-0 opacity-0'}`}
                      ></div>
                      <span>{num.slice(0, -2)}<sup>{num.slice(-2)}</sup> Saturday</span>
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400 mt-3 italic">Multiple Saturdays can be selected for the month.</p>
              </div>


              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={shiftFormData.description}
                  onChange={handleShiftInputChange}
                  rows={3}
                  placeholder="Enter shift description or notes (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand resize-none"
                />
                <div className="flex justify-end text-xs text-gray-400 mt-1">
                  <span>{shiftFormData.description.length}/{TEXT_LIMITS.description}</span>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center space-x-2 pt-2">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={shiftFormData.isActive}
                  onChange={handleShiftInputChange}
                  className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded focus:outline-none"
                />
                <label className="text-sm font-medium text-gray-700">
                  Active Shift
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
              <Button
                type="button"
                variant="secondary"
                onClick={resetShiftForm}
                className="flex-1"
                aria-label="Cancel shift"
              >
                Cancel shift
              </Button>
              <Button
                type="submit"
                variant="primary"
                className="flex-1 bg-green-600 hover:bg-green-700 text-white flex items-center justify-center space-x-2 border-transparent"
                aria-label={editingShift ? "Update shift" : "Create shift"}
              >
                <Save className="h-4 w-4" />
                <span>{editingShift ? "Update shift" : "Create shift"}</span>
              </Button>
            </div>
          </div>
        </form>
      </div>
    );
  }

  if (showRosterForm) {
    return (
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={resetRosterForm}
              className="p-2"
              aria-label="Back to shift roster"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-xl font-semibold text-gray-900">
              {editingRoster
                ? "Edit Roster Assignment"
                : "New Roster Assignment"}
            </h2>
          </div>
        </div>

        <form onSubmit={handleSubmitRoster} className="p-6">
          {/* Employee Selection Section */}
          <div className="bg-brand/5 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="h-5 w-5 mr-2 text-brand" />
              Employee Assignment
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-1 gap-6">


              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Employee <span className="text-red-500">*</span>
                </label>
                <div className="relative employee-dropdown-container">
                  {/* Display selected employee or placeholder */}
                  <div
                    onClick={() => {
                      setEmployeeSearchOpen(!employeeSearchOpen);
                      if (!employeeSearchOpen) {
                        setEmployeeSearchTerm("");
                      }
                    }}
                    className={`mt-1 w-full border rounded-md px-3 py-2 bg-white cursor-pointer flex items-center justify-between hover:border-gray-400 focus-within:ring-2 focus-within:ring-brand focus-within:outline-none focus-within:border-transparent ${(rosterFormSubmitted || rosterTouched.employeeId) && rosterFormErrors.employeeId ? "border-red-300 bg-red-50" : "border-gray-300"}`}
                  >
                    <span className={rosterFormData.employeeId ? "text-gray-900" : "text-gray-500"}>
                      {rosterFormData.employeeId && selectedEmployeeData
                        ? `${selectedEmployeeData.name || ""} (${selectedEmployeeData.employeeId || selectedEmployeeData.id || ""})`
                        : rosterFormData.employeeId
                          ? `Employee ID: ${rosterFormData.employeeId}`
                          : "Select Employee"}
                    </span>
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform ${employeeSearchOpen ? "transform rotate-180" : ""
                        }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>

                  {/* Dropdown with search */}
                  {employeeSearchOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden">
                      {/* Search input */}
                      <div className="p-2 border-b border-gray-200">
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search by employee name..."
                            value={employeeSearchTerm}
                            onChange={(e) => {
                              // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                              const value = e.target.value.replace(INVALID_CHARS_REGEX, '').slice(0, 100);
                              setEmployeeSearchTerm(value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            maxLength={100}
                            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand text-sm"
                            autoFocus
                          />
                        </div>
                      </div>

                      {/* Results list */}
                      <div className="max-h-64 overflow-y-auto">
                        {isLoadingEmployeeSearch && debouncedEmployeeSearch.trim() ? (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            Searching...
                          </div>
                        ) : dropdownEmployees.length > 0 ? (
                          dropdownEmployees.map((employee) => (
                            <div
                              key={employee.id}
                              onClick={() => {
                                setRosterFormData({
                                  ...rosterFormData,
                                  employeeId: employee.id,
                                });
                                // Mark as touched and trigger validation
                                setRosterTouched(prev => ({ ...prev, employeeId: true }));
                                const errors = validateRosterForm({ ...rosterFormData, employeeId: employee.id }, editingRoster);
                                setRosterFormErrors(prev => ({ ...prev, employeeId: errors.employeeId }));

                                // Store the selected employee data for display
                                setSelectedEmployeeData(employee);
                                setEmployeeSearchOpen(false);
                                setEmployeeSearchTerm("");
                              }}
                              className={`px-4 py-2 hover:bg-brand/5 cursor-pointer border-b border-gray-100 ${rosterFormData.employeeId === employee.id
                                ? "bg-brand/5"
                                : ""
                                }`}
                            >
                              <div className="text-sm font-medium text-gray-900">
                                {employee.name || ""}
                              </div>
                              <div className="text-xs text-gray-500">
                                {employee.employeeId || employee.id || ""} -{" "}
                                {employee.email || ""}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            {debouncedEmployeeSearch.trim()
                              ? "No employees found"
                              : "Type to search employees..."}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {(rosterFormSubmitted || rosterTouched.employeeId) && rosterFormErrors.employeeId && (
                  <p className="text-xs text-red-500 mt-1">{rosterFormErrors.employeeId}</p>
                )}
                <div className="text-xs text-gray-500 mt-1">
                  {employeeSearchOpen && debouncedEmployeeSearch.trim()
                    ? `Found ${dropdownEmployees.length} employee(s)`
                    : employeeSearchOpen
                      ? "Type to search employees..."
                      : ""}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shift <span className="text-red-500">*</span>
              </label>
              <select
                name="shiftId"
                value={rosterFormData.shiftId}
                onChange={handleRosterInputChange}
                onBlur={handleRosterBlur}
                required
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${(rosterFormSubmitted || rosterTouched.shiftId) && rosterFormErrors.shiftId
                  ? "border-red-300 bg-red-50"
                  : "border-gray-300"
                  }`}
              >
                <option value="">Select shift</option>
                {shifts
                  .filter((shift) => shift.isActive)
                  .map((shift) => {
                    const optionText = `${shift.name} (${shift.startTime} - ${shift.endTime}) - ${shift.shiftType} - ${shift.workingHours}h`;
                    const truncatedText = optionText.length > 50 ? optionText.substring(0, 47) + '...' : optionText;
                    return (
                      <option key={shift.id} value={shift.id}>
                        {truncatedText}
                      </option>
                    );
                  })}
              </select>
              {(rosterFormSubmitted || rosterTouched.shiftId) && rosterFormErrors.shiftId && (
                <div className="text-xs text-red-500 mt-1">
                  {rosterFormErrors.shiftId}
                </div>
              )}
              {shifts.length === 0 && (
                <div className="text-sm text-red-600 mt-1">
                  No shifts available. Please create a shift first.
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="startDate"
                value={rosterFormData.startDate}
                onChange={handleRosterInputChange}
                onBlur={handleRosterBlur}
                onClick={(e) => e.target.showPicker?.()}
                required
                min={
                  editingRoster && editingRoster.originalStartDate
                    ? editingRoster.originalStartDate
                    : new Date().toISOString().split("T")[0]
                }
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${(rosterFormSubmitted || rosterTouched.startDate) && rosterFormErrors.startDate
                  ? "border-red-300 bg-red-50"
                  : "border-gray-300"
                  }`}
              />
              {(rosterFormSubmitted || rosterTouched.startDate) && rosterFormErrors.startDate && (
                <p className="text-xs text-red-500 mt-1">{rosterFormErrors.startDate}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="endDate"
                value={rosterFormData.endDate}
                onChange={handleRosterInputChange}
                onBlur={handleRosterBlur}
                onClick={(e) => e.target.showPicker?.()}
                required
                min={rosterFormData.startDate}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${(rosterFormSubmitted || rosterTouched.endDate) && rosterFormErrors.endDate
                  ? "border-red-300 bg-red-50"
                  : "border-gray-300"
                  }`}
              />
              {(rosterFormSubmitted || rosterTouched.endDate) && rosterFormErrors.endDate && (
                <p className="text-xs text-red-500 mt-1">{rosterFormErrors.endDate}</p>
              )}
            </div>
          </div>


          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              name="notes"
              value={rosterFormData.notes}
              onChange={handleRosterInputChange}
              rows={3}
              maxLength={1500}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
            />
            <div className="flex justify-end text-xs text-gray-400 mt-1">
              {rosterFormData.notes.length}/1500 characters
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <Button type="button" variant="secondary" onClick={resetRosterForm} aria-label="Cancel roster assignment">
              Cancel assignment
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white border-transparent"
              aria-label={editingRoster ? "Update roster assignment" : "Create roster assignment"}
            >
              <Save className="h-4 w-4" />
              <span>
                {editingRoster ? "Update assignment" : "Create assignment"}
              </span>
            </Button>
          </div>
        </form>
      </div>
    );
  }

  // Manual Employee Assignment Form
  if (showManualAssignment) {
    return (
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowManualAssignment(false)}
              className="p-2"
              aria-label="Back to shift roster"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-xl font-semibold text-gray-900">
              Manual Employee Assignment
            </h2>
          </div>
        </div>

        <div className="p-6">
          {/* Permission Check - Temporarily disabled for testing */}
          {false && !canAssignManually() ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <div className="text-red-600 mb-2">
                <Users className="h-12 w-12 mx-auto mb-4" />
                <h3 className="text-lg font-semibold">Access Denied</h3>
                <p className="text-sm">
                  You don't have permission to manually assign employees to
                  shifts.
                </p>
                <p className="text-xs text-gray-600 mt-2">
                  Only HR Manager, HR Executive, and Super Admin can perform
                  manual assignments.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Bulk Assignment Section */}
              <div className="bg-brand/5 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Users className="h-5 w-5 mr-2 text-brand" />
                  Bulk Employee Assignment
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Assign multiple employees to a shift at once
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Department
                    </label>
                    <select
                      name="department"
                      value={bulkFormData.department}
                      onChange={handleBulkInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand capitalize"
                    >
                      <option value="">All Departments</option>
                      {departments.map((dept) => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Shift *
                    </label>
                    <select
                      name="shiftId"
                      value={bulkFormData.shiftId}
                      onChange={handleBulkInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                    >
                      <option value="">Choose a shift</option>
                      {shifts
                        .filter((shift) => shift.isActive)
                        .map((shift) => {
                          const optionText = `${shift.name} (${shift.startTime} - ${shift.endTime})`;
                          const truncatedText = optionText.length > 50 ? optionText.substring(0, 47) + '...' : optionText;
                          return (
                            <option key={shift.id} value={shift.id}>
                              {truncatedText}
                            </option>
                          );
                        })}
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Employees *
                  </label>
                  <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-3 bg-white">
                    {employees
                      .filter(
                        (employee) =>
                          !bulkFormData.department ||
                          employee.department === bulkFormData.department
                      )
                      .map((employee) => (
                        <label
                          key={employee.id}
                          className="flex items-center space-x-3 p-2 hover:bg-blue-50/30 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={bulkFormData.selectedEmployees.includes(
                              employee.id
                            )}
                            onChange={() =>
                              handleBulkEmployeeToggle(employee.id)
                            }
                            className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
                          />
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {employee.fullName ||
                                `${employee.firstName} ${employee.lastName}`}
                            </div>
                            <div className="text-xs text-gray-500">
                              {employee.department} • {employee.designation}
                            </div>
                          </div>
                        </label>
                      ))}
                    {employees.filter(
                      (employee) =>
                        !bulkFormData.department ||
                        employee.department === bulkFormData.department
                    ).length === 0 && (
                        <div className="text-sm text-gray-500 text-center py-4">
                          {bulkFormData.department
                            ? `No employees found in ${bulkFormData.department} department`
                            : "No employees available"}
                        </div>
                      )}
                  </div>
                  <div className="mt-2 text-sm text-gray-600">
                    Selected: {bulkFormData.selectedEmployees.length}{" "}
                    employee(s)
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      name="startDate"
                      value={bulkFormData.startDate}
                      onChange={handleBulkInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date *
                    </label>
                    <input
                      type="date"
                      name="endDate"
                      value={bulkFormData.endDate}
                      onChange={handleBulkInputChange}
                      min={bulkFormData.startDate}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <Button
                    variant="secondary"
                    onClick={() => setShowManualAssignment(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    disabled={
                      bulkFormData.selectedEmployees.length === 0 ||
                      !bulkFormData.shiftId ||
                      !bulkFormData.startDate ||
                      !bulkFormData.endDate
                    }
                    className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white border-transparent disabled:opacity-50"
                    title="Assign selected employees to shifts"
                    onClick={handleBulkSubmit}
                  >
                    <Users className="h-4 w-4" />
                    <span>
                      Assign Selected Employees (
                      {bulkFormData.selectedEmployees.length})
                    </span>
                  </Button>
                </div>
              </div>

              {/* Individual Assignment Section */}
              <div className="bg-brand/5 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <User className="h-5 w-5 mr-2 text-brand" />
                  Individual Employee Assignment
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Assign a single employee to a specific shift
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Employee *
                    </label>
                    <select
                      name="employeeId"
                      value={rosterFormData.employeeId}
                      onChange={handleRosterInputChange}
                      required
                      className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${formSubmitted && !rosterFormData.employeeId
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                        }`}
                    >
                      <option value="">Select employee</option>
                      {employees.length > 0 ? (
                        employees.map((employee) => {
                          return (
                            <option key={employee.id} value={employee.id}>
                              {employee.fullName ||
                                `${employee.firstName} ${employee.lastName}`}{" "}
                              - {employee.department} ({employee.designation})
                            </option>
                          );
                        })
                      ) : (
                        <option value="" disabled>
                          Loading employees...
                        </option>
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Shift *
                    </label>
                    <select
                      name="shiftId"
                      value={rosterFormData.shiftId}
                      onChange={handleRosterInputChange}
                      required
                      className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${formSubmitted && !rosterFormData.shiftId
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                        }`}
                    >
                      <option value="">Select shift</option>
                      {shifts
                        .filter((shift) => shift.isActive)
                        .map((shift) => {
                          const optionText = `${shift.name} (${shift.startTime} - ${shift.endTime})`;
                          const truncatedText = optionText.length > 50 ? optionText.substring(0, 50) + '...' : optionText;
                          return (
                            <option key={shift.id} value={shift.id}>
                              {truncatedText}
                            </option>
                          );
                        })}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assignment Type
                    </label>
                    <select
                      name="assignmentType"
                      value={rosterFormData.assignmentType || "Permanent"}
                      onChange={handleRosterInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                    >
                      <option value="Permanent">Permanent</option>
                      <option value="Temporary">Temporary</option>
                      <option value="Overtime">Overtime</option>
                      <option value="Replacement">Replacement</option>
                    </select>
                  </div>
                </div>

                {/* Date Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      name="startDate"
                      value={rosterFormData.startDate}
                      onChange={handleRosterInputChange}
                      required
                      min={
                        editingRoster && editingRoster.originalStartDate
                          ? editingRoster.originalStartDate
                          : new Date().toISOString().split("T")[0]
                      }
                      className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${formSubmitted && !rosterFormData.startDate
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                        }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date *
                    </label>
                    <input
                      type="date"
                      name="endDate"
                      value={rosterFormData.endDate}
                      onChange={handleRosterInputChange}
                      required
                      min={rosterFormData.startDate}
                      className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${formSubmitted && !rosterFormData.endDate
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                        }`}
                    />
                  </div>
                </div>

                {/* Notes Field */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    name="notes"
                    value={rosterFormData.notes}
                    onChange={handleRosterInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                    placeholder="Add any additional notes for this assignment..."
                  />
                </div>

                {/* Form Validation Message for Individual Assignment - Only show after user interaction */}
                {formSubmitted && !isRosterFormValid() && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs">!</span>
                      </div>
                      <div className="text-sm text-yellow-800">
                        <strong>Please complete all required fields:</strong>
                        <ul className="mt-1 ml-4 list-disc">
                          {!rosterFormData.employeeId && (
                            <li>Select an employee</li>
                          )}
                          {!rosterFormData.shiftId && <li>Select a shift</li>}
                          {!rosterFormData.startDate && (
                            <li>Choose start date</li>
                          )}
                          {!rosterFormData.endDate && <li>Choose end date</li>}
                          {rosterFormData.startDate &&
                            rosterFormData.endDate &&
                            new Date(rosterFormData.endDate) <
                            new Date(rosterFormData.startDate) && (
                              <li>End date must be after start date</li>
                            )}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
                {formSubmitted && isRosterFormValid() && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-green-400 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <div className="text-sm text-green-800">
                        <strong>Form is ready!</strong> All required fields are
                        filled.
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-4 flex justify-end space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowManualAssignment(false)}
                    className="border-brand/30 text-brand hover:bg-brand/5"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    disabled={!isRosterFormValid()}
                    className={`flex items-center space-x-2 ${isRosterFormValid()
                      ? "bg-green-600 hover:bg-green-700 text-white"
                      : "bg-gray-400 cursor-not-allowed"
                      }`}
                    title={
                      !isRosterFormValid()
                        ? "Please fill all required fields (Employee, Shift, Start Date, End Date)"
                        : "Assign employee to shift"
                    }
                    onClick={handleSubmitRoster}
                  >
                    <User className="h-4 w-4" />
                    <span>Assign Employee</span>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-gray-900">
              Shift & Roster Management
            </h2>
          </div>
          <div className="flex items-center space-x-4">
            <PermissionGate permission="attendance" action="read">
              <div className="relative" ref={selectedExportWrapRef}>
                <Button
                  variant="outline"
                  icon={
                    exportLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand border-t-transparent" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )
                  }
                  onClick={() => {
                    if (viewMode === "roster") {
                      handleExportRosterClick();
                    } else {
                      handleExportShiftsClick();
                    }
                  }}
                  disabled={exportLoading}
                  className={`flex-shrink-0 ${
                    (viewMode === "roster" && selectedRosterRows.size > 0) ||
                    (viewMode === "shifts" && selectedShiftRows.size > 0)
                      ? 'bg-brand/5 border-brand/20 text-brand'
                      : ''
                  }`}
                  aria-label={
                    viewMode === "roster"
                      ? selectedRosterRows.size > 0
                        ? `Export ${selectedRosterRows.size} selected roster rows`
                        : 'Export roster'
                      : selectedShiftRows.size > 0
                        ? `Export ${selectedShiftRows.size} selected shifts`
                        : 'Export shifts'
                  }
                >
                  {viewMode === "roster" && selectedRosterRows.size > 0
                    ? `Export Selected (${selectedRosterRows.size})`
                    : viewMode === "shifts" && selectedShiftRows.size > 0
                      ? `Export Selected (${selectedShiftRows.size})`
                      : "Export"}
                </Button>
                <SelectedExportFormatBar
                  show={
                    (viewMode === "roster" && selectedRosterRows.size > 0 && showInlineSelectedFormats) ||
                    (viewMode === "shifts" && selectedShiftRows.size > 0 && showInlineSelectedFormats)
                  }
                  panelEntered={inlineFormatPanelEntered}
                  disabled={exportLoading}
                  onSelect={(format) => {
                    if (viewMode === "roster") {
                      handleRosterSelectedFormatExport(format);
                    } else {
                      handleShiftSelectedFormatExport(format);
                    }
                  }}
                />
              </div>
            </PermissionGate>
            {/* Action Buttons - Context aware */}
            <div className="flex items-center space-x-2">
              {viewMode === "roster" && selectedRosterRows.size > 0 && (
                <PermissionGate permission="attendance" action="delete">
                  <Button
                    variant="primary"
                    icon={<Trash2 className="h-4 w-4" />}
                    onClick={handleBulkDeleteRoster}
                    className="bg-red-600 hover:bg-red-700 border-red-600 flex items-center gap-2"
                  >
                    Delete ({selectedRosterRows.size})
                  </Button>
                </PermissionGate>
              )}
              {viewMode === "shifts" && selectedShiftRows.size > 0 && (
                <PermissionGate permission="attendance" action="delete">
                  <Button
                    variant="primary"
                    icon={<Trash2 className="h-4 w-4" />}
                    onClick={handleBulkDeleteShifts}
                    className="bg-red-600 hover:bg-red-700 border-red-600 flex items-center gap-2"
                  >
                    Delete ({selectedShiftRows.size})
                  </Button>
                </PermissionGate>
              )}
              {viewMode === "shifts" ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleNewShift}
                  className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white border-transparent"
                >
                  <Plus className="h-4 w-4" />
                  <span>New Shift</span>
                </Button>
              ) : (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowRosterForm(true)}
                  className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white border-transparent"
                >
                  <Plus className="h-4 w-4" />
                  <span>Assign Roster</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex space-x-1 px-6">
          <button
            onClick={() => setViewMode("roster")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${viewMode === "roster"
              ? "border-brand text-brand"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
          >
            Roster
          </button>
          <button
            onClick={() => setViewMode("shifts")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${viewMode === "shifts"
              ? "border-brand text-brand"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
          >
            Shifts
          </button>
        </div>
      </div>

      {viewMode === "roster" && (
        <>
          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex flex-wrap items-end gap-4 w-full">
              {/* Search */}
              <div className="relative flex-[2] min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  ref={rosterSearchInputRef}
                  autoFocus
                  type="text"
                  placeholder="Search by Employee Name or Email ID"
                  value={searchTerm}
                  onChange={(e) => {
                    // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                    const value = e.target.value.replace(INVALID_CHARS_REGEX, '').slice(0, 100);
                    setSearchTerm(value);
                  }}
                  maxLength={100}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                />
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      if (rosterSearchInputRef.current) {
                        setTimeout(() => {
                          rosterSearchInputRef.current?.focus();
                        }, 0);
                      }
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Date Filter */}
              <div className="flex-shrink-0">
                <DateRangeFilter
                  startDate={filterStartDate}
                  endDate={filterEndDate}
                  allowFutureDates={true}
                  onChange={(range) => {
                    setFilterStartDate(range.startDate);
                    setFilterEndDate(range.endDate);
                    setCurrentPage(1);
                  }}
                />
              </div>

              {/* Status Filter */}
              <div className="flex-shrink-0">
                <select
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                >
                  <option value="all">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Upcoming">Upcoming</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>

              {/* Clear Filters Button */}
              {(searchTerm ||
                filterStatus !== "all" ||
                filterStartDate ||
                filterEndDate) && (
                  <div className="flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSearchTerm("");
                        setFilterStatus("all");
                        setFilterStartDate("");
                        setFilterEndDate("");
                        setCurrentPage(1);
                      }}
                      className="flex items-center space-x-2 text-gray-600 hover:text-brand border-gray-300 hover:border-brand px-4 h-[42px]"
                    >
                      <RotateCcw className="h-4 w-4" />
                      <span>Clear</span>
                    </Button>
                  </div>
                )}
            </div>
          </div>

          {/* Roster Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-transparent" style={{ backgroundColor: 'rgba(59, 130, 246, 0.14)' }}>
                <tr>
                  <th className="px-3 py-2 text-left">
                    <input
                      type="checkbox"
                      checked={filteredRoster.length > 0 && selectedRosterRows.size === filteredRoster.length}
                      onChange={handleSelectAllRoster}
                      className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-brand cursor-pointer"
                    />
                  </th>
                  <th
                    className="px-3 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                    onClick={() => handleSort('employee')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Employee</span>
                      {sortField === 'employee' ? (
                        sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-brand" /> : <ArrowDown className="h-3 w-3 text-brand" />
                      ) : <ArrowUpDown className="h-3 w-3 text-gray-400" />}
                    </div>
                  </th>
                  <th
                    className="px-3 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                    onClick={() => handleSort('shift')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Shift</span>
                      {sortField === 'shift' ? (
                        sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-brand" /> : <ArrowDown className="h-3 w-3 text-brand" />
                      ) : <ArrowUpDown className="h-3 w-3 text-gray-400" />}
                    </div>
                  </th>
                  <th
                    className="px-3 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                    onClick={() => handleSort('schedule')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Schedule</span>
                      {sortField === 'schedule' ? (
                        sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-brand" /> : <ArrowDown className="h-3 w-3 text-brand" />
                      ) : <ArrowUpDown className="h-3 w-3 text-gray-400" />}
                    </div>
                  </th>
                  <th
                    className="px-3 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                    onClick={() => handleSort('appliedDate')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Applied Date</span>
                      {sortField === 'appliedDate' ? (
                        sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-brand" /> : <ArrowDown className="h-3 w-3 text-brand" />
                      ) : <ArrowUpDown className="h-3 w-3 text-gray-400" />}
                    </div>
                  </th>
                  <th
                    className="px-3 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortField === 'status' ? (
                        sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-brand" /> : <ArrowDown className="h-3 w-3 text-brand" />
                      ) : <ArrowUpDown className="h-3 w-3 text-gray-400" />}
                    </div>
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mb-4"></div>
                        <p className="text-sm text-gray-500">Loading roster data...</p>
                      </div>
                    </td>
                  </tr>
                ) : paginatedRoster.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <div className="text-gray-500">
                        <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                        <p className="text-sm">No roster assignments found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  getSortedData(paginatedRoster, 'roster').map((entry) => (
                    <tr key={entry.id} className="hover:bg-blue-50/30">
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selectedRosterRows.has(entry.id)}
                          onChange={() => handleSelectRosterRow(entry.id)}
                          className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-brand cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <div>
                          <div className="text-sm font-medium text-gray-900 truncate max-w-xs capitalize">
                            {entry.employeeName || "Unknown Employee"}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {entry.employeeEmail || "N/A"}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-xs capitalize">
                          {entry.shiftName || "Unknown Shift"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {entry.shiftGraceCheckInTime || 0}m grace · {entry.shiftGraceDays || 0}d ({entry.shiftGraceDaysTime || 0}m)/mo
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center space-x-1">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span>
                            {entry.startDate
                              ? formatDate(entry.startDate, { includeTime: false })
                              : "N/A"}{" "}
                            -
                            {entry.endDate
                              ? formatDate(entry.endDate, { includeTime: false })
                              : "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                        {entry.createdAt ? formatDate(entry.createdAt, { includeTime: true }) : "N/A"}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${entry.status === "Active"
                            ? "bg-green-100 text-green-800"
                            : entry.status === "Upcoming"
                              ? "bg-blue-100 text-blue-800"
                              : entry.status === "Completed"
                                ? "bg-gray-100 text-gray-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                        >
                          {entry.status || "Unknown"}
                        </span>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <PermissionGate permission="attendance" action="read">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewDetails(entry)}
                              className="text-brand hover:text-brand-dark"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate permission="attendance" action="update">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditRoster(entry)}
                              className="text-green-600 hover:text-green-900"
                              title="Edit Roster"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate permission="attendance" action="delete">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteRoster(entry)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Roster"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {/* Pagination Controls - integrated into the table card */}
            {(() => {
              const rosterTotalItems = Number(pagination?.totalItems || paginatedRoster.length || 0);
              const rosterTotalPages = Number(pagination?.totalPages || 1);
              const effectivePaginationLimit = pagination?.itemsPerPage || pagination?.limit || itemsPerPage || 10;
              const currentPageNum = pagination?.currentPage || currentPage || 1;
              const shouldShowPagination = !loading;

              // Use hasPrevPage and hasNextPage from pagination state (calculated from API response)
              const hasPrevPage = pagination?.hasPrevPage ?? (currentPageNum > 1);
              const hasNextPage = pagination?.hasNextPage ?? (currentPageNum < rosterTotalPages);

              return shouldShowPagination ? (
                  <PaginationControls
                    currentPage={currentPageNum}
                    totalPages={rosterTotalPages}
                    totalItems={rosterTotalItems}
                    limit={effectivePaginationLimit}
                    hasNextPage={hasNextPage}
                    hasPrevPage={hasPrevPage}
                    onPageChange={handlePageChange}
                    onLimitChange={handleItemsPerPageChange}
                    limitOptions={[10, 15, 20, 50]}
                    border={false}
                  />
              ) : null;
            })()}
          </div>
        </>
      )}

      {/* Shifts Management View */}
      {viewMode === "shifts" && (
        <>
          {/* Shifts Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-transparent" style={{ backgroundColor: 'rgba(59, 130, 246, 0.14)' }}>
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={shifts.length > 0 && selectedShiftRows.size === shifts.length}
                      onChange={handleSelectAllShifts}
                      className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-brand cursor-pointer"
                    />
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Shift Name</span>
                      {sortField === 'name' ? (
                        sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-brand" /> : <ArrowDown className="h-3 w-3 text-brand" />
                      ) : <ArrowUpDown className="h-3 w-3 text-gray-400" />}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                    onClick={() => handleSort('shiftType')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Type</span>
                      {sortField === 'shiftType' ? (
                        sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-brand" /> : <ArrowDown className="h-3 w-3 text-brand" />
                      ) : <ArrowUpDown className="h-3 w-3 text-gray-400" />}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                    onClick={() => handleSort('time')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Time</span>
                      {sortField === 'time' ? (
                        sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-brand" /> : <ArrowDown className="h-3 w-3 text-brand" />
                      ) : <ArrowUpDown className="h-3 w-3 text-gray-400" />}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                    onClick={() => handleSort('workingHours')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Total Working Hours</span>
                      {sortField === 'workingHours' ? (
                        sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-brand" /> : <ArrowDown className="h-3 w-3 text-brand" />
                      ) : <ArrowUpDown className="h-3 w-3 text-gray-400" />}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                    onClick={() => handleSort('breakDuration')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Break Duration</span>
                      {sortField === 'breakDuration' ? (
                        sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-brand" /> : <ArrowDown className="h-3 w-3 text-brand" />
                      ) : <ArrowUpDown className="h-3 w-3 text-gray-400" />}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Grace Rules
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                    onClick={() => handleSort('appliedDate')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Applied Date</span>
                      {sortField === 'appliedDate' ? (
                        sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-brand" /> : <ArrowDown className="h-3 w-3 text-brand" />
                      ) : <ArrowUpDown className="h-3 w-3 text-gray-400" />}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortField === 'status' ? (
                        sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-brand" /> : <ArrowDown className="h-3 w-3 text-brand" />
                      ) : <ArrowUpDown className="h-3 w-3 text-gray-400" />}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getSortedData(shifts, 'shift').map((shift) => (
                  <tr key={shift.id} className="hover:bg-blue-50/30">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedShiftRows.has(shift.id)}
                        onChange={() => handleSelectShiftRow(shift.id)}
                        className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-brand cursor-pointer"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 truncate max-w-xs capitalize">
                        {shift.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-full capitalize">
                        {shift.shiftType || "Regular"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatTimeAMPM(shift.startTime)} - {formatTimeAMPM(shift.endTime)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {(() => {
                          const hours = shift.workingHours || shift.totalWorkingHours;
                          if (hours) {
                            return typeof hours === 'number' ? `${hours.toFixed(1)} hours` : `${hours} hours`;
                          }
                          // Calculate if not present
                          if (shift.startTime && shift.endTime) {
                            const start = new Date(`2024-01-01T${shift.startTime}:00`);
                            const end = new Date(`2024-01-01T${shift.endTime}:00`);
                            if (end < start) {
                              end.setDate(end.getDate() + 1);
                            }
                            const diffMs = end - start;
                            const diffHours = diffMs / (1000 * 60 * 60);
                            const calculated = Math.max(0, diffHours);
                            return Number.isFinite(calculated) ? `${calculated.toFixed(1)} hours` : "N/A";
                          }
                          return "N/A";
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {shift.breakDuration || 0} min
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                        {shift.graceCheckInTime || 0}m in · {shift.graceDays || 0}d ({shift.graceDaysTime || 0}m)/mo
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {shift.createdAt ? formatDate(shift.createdAt, { includeTime: true }) : "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${shift.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                          }`}
                      >
                        {shift.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewShiftDetail(shift)}
                          className="text-brand hover:text-brand-dark hover:bg-brand/5"
                          title="View Shift Details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditShift(shift)}
                          className="text-brand hover:text-blue-900 hover:bg-blue-50"
                          title="Edit Shift"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleShiftStatus(shift.id)}
                          className={`p-1.5 rounded-full transition-colors ${shift.isActive
                            ? "text-green-600 hover:bg-green-50"
                            : "text-yellow-600 hover:bg-yellow-50"
                            }`}
                          title={shift.isActive ? "Deactivate Shift" : "Activate Shift"}
                          aria-label={shift.isActive ? "Deactivate shift" : "Activate shift"}
                        >
                          {shift.isActive ? (
                            <ToggleRight className="h-5 w-5" />
                          ) : (
                            <ToggleLeft className="h-5 w-5" />
                          )}
                        </Button>
                        <PermissionGate permission="attendance" action="delete">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleDeleteShift(shift)}
                            className="text-red-600 hover:text-red-900 hover:bg-red-50"
                            title="Delete Shift"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </PermissionGate>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {shifts.length === 0 && (
            <div className="text-center py-12">
              <div className="mx-auto h-12 w-12 text-gray-400">
                <Clock className="h-12 w-12" />
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No shifts found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by creating a new shift.
              </p>
              <div className="mt-6">
                <Button
                  variant="primary"
                  onClick={handleNewShift}
                  className="flex items-center space-x-2 mx-auto bg-green-600 hover:bg-green-700 text-white border-transparent"
                >
                  <Plus className="h-4 w-4" />
                  <span>Create New Shift</span>
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Empty State - Shifts view removed */}
      {viewMode === "roster" && !loading && pagination.totalItems === 0 && (
        <div className="text-center py-12">
          <div className="mx-auto h-12 w-12 text-gray-400">
            <Users className="h-12 w-12" />
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No roster assignments found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by assigning employees to shifts.
          </p>
          <div className="mt-6">
            <Button
              variant="primary"
              onClick={() => setShowRosterForm(true)}
              className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white border-transparent"
            >
              <Plus className="h-4 w-4" />
              <span>Assign Roster</span>
            </Button>
          </div>
        </div>
      )}

      {/* WarningPopup for Delete confirmation */}
      <WarningPopup
        isOpen={showDeleteShiftModal}
        onClose={() => {
          setShowDeleteShiftModal(false);
          setShiftToDelete(null);
          setIsBulkDelete(false);
        }}
        onConfirm={confirmDeleteShift}
        title={isBulkDelete ? "Delete Shifts" : "Delete Shift"}
        message={
          isBulkDelete
            ? `Are you sure you want to delete ${selectedShiftRows.size} selected shifts? This action cannot be undone.`
            : `Are you sure you want to delete the shift "${shiftToDelete?.name}"? This action cannot be undone.`
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      <WarningPopup
        isOpen={showDeleteRosterModal}
        onClose={() => {
          setShowDeleteRosterModal(false);
          setRosterToDelete(null);
          setIsBulkDelete(false);
        }}
        onConfirm={confirmDeleteRoster}
        title={isBulkDelete ? "Delete Roster Assignments" : "Delete Roster Assignment"}
        message={
          isBulkDelete
            ? `Are you sure you want to delete ${selectedRosterRows.size} selected roster assignments? This action cannot be undone.`
            : `Are you sure you want to delete this roster assignment for ${rosterToDelete?.employeeName}? This action cannot be undone.`
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      {
        showShiftDetail && selectedShiftDetail && (
          <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center px-4 py-6">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex-1 min-w-0 pr-4">
                  <p className="text-sm text-gray-500 tracking-wide">Shift Detail</p>
                  <h2 className="text-2xl font-semibold text-gray-900 break-words whitespace-normal capitalize">{selectedShiftDetail.name}</h2>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-sm text-gray-500">{selectedShiftDetail.shiftType || 'Regular'} Shift</p>
                    {selectedShiftDetail.isFlexible && (
                      <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-[#0B2033] rounded-md border border-blue-200">
                        Flexible
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowShiftDetail(false);
                    setSelectedShiftDetail(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close shift detail"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500">Shift Time</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatTimeAMPM(selectedShiftDetail.startTime)} - {formatTimeAMPM(selectedShiftDetail.endTime)}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500">Total Working Hours</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {(() => {
                        // Priority: stored workingHours (Net), then manual calculation using helper
                        const hours = selectedShiftDetail.workingHours ||
                          calculateWorkingHours(
                            selectedShiftDetail.startTime,
                            selectedShiftDetail.endTime,
                            selectedShiftDetail.breakDuration
                          );
                        return hours !== null ? `${hours} hours` : 'N/A';
                      })()}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500">Break Duration</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedShiftDetail.breakDuration || 0} minutes
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500">Status</p>
                    <span
                      className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold capitalize ${selectedShiftDetail.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                    >
                      {selectedShiftDetail.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-4 border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Attendance Rules</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Grace Check-in</p>
                      <p className="text-sm font-medium text-gray-900">{selectedShiftDetail.graceCheckInTime || 0} min</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Grace Days Allowance</p>
                      <p className="text-sm font-medium text-gray-900">{selectedShiftDetail.graceDays || 0} days/month</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Grace Days Timing</p>
                      <p className="text-sm font-medium text-gray-900">{selectedShiftDetail.graceDaysTime || 0} min</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Grace Check-out</p>
                      <p className="text-sm font-medium text-gray-900">{selectedShiftDetail.graceCheckOutTime || 0} min</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Half Day Rule</p>
                      <p className="text-sm font-medium text-gray-900">{selectedShiftDetail.halfDayRule || 0} hr</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Absent Rule</p>
                      <p className="text-sm font-medium text-gray-900">{selectedShiftDetail.absentRule || 0} hr</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-4 border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Description</h4>
                  <p className="text-sm text-gray-700 break-words whitespace-pre-wrap first-letter:capitalize">
                    {selectedShiftDetail.description || 'No description provided.'}
                  </p>
                </div>
                <div className="space-y-6">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <div className="w-1 h-3 bg-green-500 rounded-full"></div>
                      Working Days
                    </h4>
                    <div className="flex flex-wrap gap-2.5">
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                        <div
                          key={day}
                          className={`px-3.5 py-1.5 text-xs font-semibold rounded-xl border-2 transition-all duration-300 ${selectedShiftDetail.workingDays?.includes(day)
                            ? "bg-green-50 border-green-200 text-green-700 shadow-sm"
                            : "bg-gray-50 border-gray-100/50 text-gray-400 opacity-60"
                            }`}
                        >
                          {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <div className="w-1 h-3 bg-red-500 rounded-full"></div>
                      Weekly Holidays
                    </h4>
                    <div className="flex flex-wrap gap-2.5">
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                        <div
                          key={day}
                          className={`px-3.5 py-1.5 text-xs font-semibold rounded-xl border-2 transition-all duration-300 ${selectedShiftDetail.weeklyHolidays?.includes(day)
                            ? "bg-red-50 border-red-200 text-red-700 shadow-sm"
                            : "bg-gray-50 border-gray-100/50 text-gray-400 opacity-60"
                            }`}
                        >
                          {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <div className="w-1 h-3 bg-brand rounded-full"></div>
                      Saturday Working
                    </h4>
                    <div className="flex flex-wrap gap-2.5">
                      {['1st', '2nd', '3rd', '4th', '5th'].map((num, index) => (
                        <div
                          key={num}
                          className={`px-3.5 py-1.5 text-xs font-semibold rounded-xl border-2 transition-all duration-300 flex items-center gap-2 ${selectedShiftDetail.saturdayWorking?.includes(index + 1)
                            ? "bg-brand/5 border-brand text-brand shadow-sm"
                            : "bg-gray-50 border-gray-100/50 text-gray-400 opacity-60"
                            }`}
                        >
                          <div className={`w-1.5 h-1.5 rounded-full ${selectedShiftDetail.saturdayWorking?.includes(index + 1) ? 'bg-brand shadow-[0_0_8px_rgba(var(--color-brand),0.5)]' : 'bg-transparent'}`}></div>
                          <span>{num.slice(0, -2)}<sup>{num.slice(-2)}</sup> Saturday</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-100">
                    <h4 className="text-sm font-semibold text-gray-900 mb-4">Audit Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Created By Details */}
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold mb-3">Created By</p>
                        {selectedShiftDetail.createdBy && typeof selectedShiftDetail.createdBy === 'object' ? (
                          <div className="space-y-2">
                            <p className="text-sm font-bold text-gray-900">
                              {selectedShiftDetail.createdBy.firstName} {selectedShiftDetail.createdBy.lastName}
                            </p>
                            <div className="grid grid-cols-1 gap-1">
                              <p className="text-xs text-gray-600 flex items-center">
                                <span className="w-16 text-gray-400">Emp ID:</span>
                                <span className="font-medium text-gray-700">{selectedShiftDetail.createdBy.employeeId || 'N/A'}</span>
                              </p>
                              <p className="text-xs text-gray-600 flex items-center">
                                <span className="w-16 text-gray-400">Role:</span>
                                <span className="font-medium text-gray-700 capitalize">{selectedShiftDetail.createdBy.role?.replace('_', ' ') || 'N/A'}</span>
                              </p>
                              <p className="text-xs text-gray-600 flex items-center">
                                <span className="w-16 text-gray-400">Dept:</span>
                                <span className="font-medium text-gray-700">{selectedShiftDetail.createdBy.department || 'N/A'}</span>
                              </p>
                            </div>
                            <div className="pt-2 mt-2 border-t border-gray-200/50">
                              <p className="text-[10px] text-gray-400 italic">
                                {selectedShiftDetail.createdAt ? formatDate(selectedShiftDetail.createdAt) : 'N/A'}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 italic">Record metadata unavailable</p>
                        )}
                      </div>

                      {/* Updated By Details */}
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold mb-3">Last Updated By</p>
                        {selectedShiftDetail.updatedBy && typeof selectedShiftDetail.updatedBy === 'object' ? (
                          <div className="space-y-2">
                            <p className="text-sm font-bold text-gray-900">
                              {selectedShiftDetail.updatedBy.firstName} {selectedShiftDetail.updatedBy.lastName}
                            </p>
                            <div className="grid grid-cols-1 gap-1">
                              <p className="text-xs text-gray-600 flex items-center">
                                <span className="w-16 text-gray-400">Emp ID:</span>
                                <span className="font-medium text-gray-700">{selectedShiftDetail.updatedBy.employeeId || 'N/A'}</span>
                              </p>
                              <p className="text-xs text-gray-600 flex items-center">
                                <span className="w-16 text-gray-400">Role:</span>
                                <span className="font-medium text-gray-700 capitalize">{selectedShiftDetail.updatedBy.role?.replace('_', ' ') || 'N/A'}</span>
                              </p>
                              <p className="text-xs text-gray-600 flex items-center">
                                <span className="w-16 text-gray-400">Dept:</span>
                                <span className="font-medium text-gray-700">{selectedShiftDetail.updatedBy.department || 'N/A'}</span>
                              </p>
                            </div>
                            <div className="pt-2 mt-2 border-t border-gray-200/50">
                              <p className="text-[10px] text-gray-400 italic">
                                {selectedShiftDetail.updatedAt ? formatDate(selectedShiftDetail.updatedAt) : 'N/A'}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 italic">No updates recorded</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end">
                <button
                  onClick={() => {
                    setShowShiftDetail(false);
                    setSelectedShiftDetail(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-white transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title={viewMode === "roster" ? "Export Roster Assignments" : "Export Shifts"}
        showDateRange
        scopedFormatExport={{
          onExport: (scope, format, meta) => {
            if (viewMode === "roster") {
              handleRosterScopedExport(scope, format, meta);
            } else {
              handleShiftScopedExport(scope, format, meta);
            }
          },
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />
    </div >
  );
};

export default ShiftRoster;
